/**
 * src/data/glossaryMoi.js: nội dung các thuật ngữ bổ sung đợt 10/2026.
 *
 * Khung (tên, cụm, công cụ liên quan) khai ở glossary.js như mọi mục khác;
 * file này chỉ chứa nội dung. Cùng quy tắc viết với glossaryContent.js: câu
 * đầu trả lời thẳng, ví dụ bằng VNĐ, có phần sai lầm thường gặp, dẫn tới công
 * cụ. Cú pháp liên kết trong câu: xem src/lib/lienKetChu.js.
 */

export const NOI_DUNG_MOI = {
  /* ─── Khái niệm nền ───────────────────────────────────────────── */

  'mvp-la-gi': {
    capNhat: '2026-10-11',
    lienQuan: ['product-market-fit', 'traction-la-gi', 'trien-khai-thu', 'vong-pre-seed'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'MVP (minimum viable product, sản phẩm khả dụng tối thiểu) là phiên bản đơn giản nhất của sản phẩm mà vẫn giải quyết được vấn đề cốt lõi cho một nhóm khách hàng thật, đủ để học xem họ có thực sự cần và chịu trả tiền hay không.',
          'Chữ quan trọng là "khả dụng". MVP không phải bản nháp hỏng hóc, mà là bản làm ít tính năng nhưng làm đúng một việc, để đo phản ứng của thị trường với chi phí và thời gian thấp nhất.',
        ],
      },
      {
        h: 'MVP có thể đơn giản tới mức nào',
        li: [
          'Một trang đích mô tả sản phẩm kèm nút đăng ký hoặc đặt cọc, để đo có bao nhiêu người quan tâm thật.',
          'Làm thủ công phía sau: khách đặt hàng qua form, đội ngũ xử lý bằng tay và Excel thay vì hệ thống tự động.',
          'Một phiên bản chỉ có đúng một tính năng chính, dành cho một nhóm khách hàng hẹp.',
        ],
        sau: [
          'Ví dụ: một startup muốn làm phần mềm quản lý kho cho cửa hàng nhỏ. Thay vì mất 8 tháng và 1,5 tỷ để xây hệ thống đầy đủ, đội làm một bảng tính có mẫu sẵn cùng nhóm Zalo hỗ trợ, thu 300.000 đồng mỗi tháng từ 20 cửa hàng. Sau 2 tháng, họ biết tính năng nào khách dùng hằng ngày và tính năng nào không ai hỏi tới.',
        ],
      },
      {
        h: 'Vì sao nhà đầu tư quan tâm',
        p: [
          'Ở vòng angel và [[vong-pre-seed|pre-seed]], MVP là bằng chứng đầu tiên cho thấy đội ngũ làm ra được sản phẩm và có người dùng. Một MVP có khách trả tiền, dù ít, thuyết phục hơn nhiều so với một bản kế hoạch chi tiết. Thứ nhà đầu tư muốn xem là bạn đã học được gì từ MVP, không phải số tính năng.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Làm quá nhiều trước khi cho khách dùng. Thêm tính năng để cảm thấy "sẵn sàng" là cách phổ biến nhất để đốt tiền vào thứ không ai cần.',
          'Cho dùng miễn phí và coi lượt đăng ký là bằng chứng. Người dùng miễn phí cho biết rất ít về việc họ có trả tiền hay không.',
          'Không đặt trước câu hỏi cần trả lời. MVP là một thí nghiệm, cần biết trước kết quả nào thì đi tiếp, kết quả nào thì đổi hướng.',
        ],
      },
    ],
    faq: [
      {
        q: 'MVP là gì?',
        a: 'Là sản phẩm khả dụng tối thiểu: phiên bản đơn giản nhất vẫn giải quyết được vấn đề chính cho khách hàng thật, dùng để học xem thị trường có cần không trước khi đầu tư lớn.',
      },
      {
        q: 'MVP khác bản mẫu (prototype) thế nào?',
        a: 'Bản mẫu dùng để thử ý tưởng hoặc giao diện, thường chưa ai dùng thật. MVP được khách hàng thật sử dụng, lý tưởng nhất là trả tiền, nên cho ra số liệu thật.',
      },
      {
        q: 'Cần MVP mới gọi vốn được không?',
        a: 'Không bắt buộc ở vòng angel, nhưng có MVP và vài khách hàng đầu tiên thì cuộc nói chuyện với nhà đầu tư khác hẳn. Ở vòng pre-seed gần như ai cũng đã có.',
      },
    ],
  },

  'product-market-fit': {
    capNhat: '2026-10-11',
    lienQuan: ['mvp-la-gi', 'traction-la-gi', 'churn-rate', 'cohort-la-gi', 'nrr-net-revenue-retention'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Product-market fit (PMF) là trạng thái sản phẩm đáp ứng đúng nhu cầu của một thị trường đủ lớn, tới mức khách hàng tự tìm đến, ở lại dùng lâu và giới thiệu cho người khác, thay vì bạn phải đẩy từng người.',
          'Đây là cột mốc quan trọng nhất giữa vòng [[vong-pre-seed|pre-seed]] và [[series-a|Series A]]. Trước PMF, việc chính là tìm ra nó; sau PMF, việc chính là mở rộng.',
        ],
      },
      {
        h: 'Dấu hiệu đo được',
        li: [
          'Giữ chân ổn định: theo dõi từng nhóm khách theo tháng bắt đầu dùng ([[cohort-la-gi|cohort]]), đường giữ chân đi ngang thay vì rơi dần về 0.',
          'Tăng trưởng tự nhiên: một phần đáng kể khách mới đến từ giới thiệu hoặc tìm kiếm, không chỉ từ quảng cáo.',
          'Khách hỏi tính năng, phàn nàn khi sản phẩm lỗi: nghĩa là họ đã phụ thuộc vào nó.',
          'Một câu hỏi khảo sát hay dùng: "Bạn sẽ thấy thế nào nếu không dùng sản phẩm này nữa?". Nếu khoảng 40% người dùng thường xuyên trả lời "rất thất vọng", đó thường được coi là tín hiệu tốt.',
        ],
      },
      {
        h: 'Một ví dụ bằng số',
        p: [
          'Một ứng dụng đặt lịch cho tiệm làm tóc có 100 tiệm bắt đầu dùng trong tháng 1. Sau 3 tháng còn 62 tiệm, sau 6 tháng còn 58, sau 9 tháng vẫn 57. Đường giữ chân đã đi ngang quanh 57%. Tiệm nào qua được 3 tháng đầu thì gần như ở lại. Đây là dấu hiệu PMF với nhóm khách này, dù quy mô còn nhỏ.',
          'Ngược lại, nếu sau 9 tháng chỉ còn 15 và vẫn đang giảm, sản phẩm chưa giữ được khách, và đổ thêm tiền vào marketing chỉ làm thùng thủng to hơn.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Nhầm tăng trưởng nhờ quảng cáo với PMF. Lượt đăng ký tăng mạnh nhưng [[churn-rate|tỷ lệ rời bỏ]] cao thì chưa phải.',
          'Tuyên bố đã có PMF trong deck mà không có số liệu giữ chân đi kèm. Nhà đầu tư sẽ hỏi ngay.',
          'Mở rộng sang thị trường mới trước khi chắc chắn với thị trường đầu tiên.',
        ],
      },
    ],
    faq: [
      {
        q: 'Product-market fit là gì?',
        a: 'Là lúc sản phẩm đáp ứng đúng nhu cầu của một thị trường đủ lớn: khách tự tìm đến, ở lại lâu và giới thiệu người khác.',
      },
      {
        q: 'Làm sao biết mình đã có PMF?',
        a: 'Nhìn số giữ chân theo cohort: nếu đường giữ chân đi ngang ở một mức đáng kể thay vì rơi về 0, cộng thêm tăng trưởng từ giới thiệu, thường là dấu hiệu tốt. Không có con số chuẩn chung cho mọi ngành.',
      },
      {
        q: 'Cần có PMF mới gọi vốn được không?',
        a: 'Không ở vòng angel và pre-seed, khi nhà đầu tư đặt cược vào khả năng tìm ra PMF. Từ seed về sau thì dấu hiệu PMF ngày càng được đòi hỏi rõ.',
      },
    ],
  },

  'traction-la-gi': {
    capNhat: '2026-10-11',
    lienQuan: ['product-market-fit', 'tang-truong-mom', 'mrr-arr', 'thu-cam-ket-mua', 'trien-khai-thu'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Traction là bằng chứng định lượng cho thấy thị trường đang đón nhận sản phẩm: doanh thu, số khách trả tiền, người dùng hoạt động, tốc độ tăng của các con số đó. Nói gọn, đó là phần "đã có người cần thật" trong câu chuyện của bạn.',
          'Trong [[pitch-deck-la-gi|pitch deck]], slide traction thường quyết định nhà đầu tư có đọc tiếp nghiêm túc hay không, nhất là từ vòng [[vong-seed|seed]] trở đi.',
        ],
      },
      {
        h: 'Traction ở từng giai đoạn trông thế nào',
        bang: {
          cot: ['Giai đoạn', 'Traction thường được chấp nhận'],
          hang: [
            ['Angel', 'Danh sách chờ, vài cuộc phỏng vấn khách hàng có ghi chép, [[thu-cam-ket-mua|thư cam kết mua]]'],
            ['Pre-seed', 'MVP có người dùng, vài khách trả tiền, [[trien-khai-thu|triển khai thử]] có thu phí'],
            ['Seed', 'Doanh thu định kỳ đang tăng đều, số liệu giữ chân khách'],
            ['Series A', 'Doanh thu đáng kể, [[kinh-te-don-vi|kinh tế đơn vị]] có lãi, kênh tăng trưởng lặp lại được'],
          ],
        },
      },
      {
        h: 'Trình bày traction cho thuyết phục',
        li: [
          'Chọn một chỉ số chính phản ánh giá trị thật, ví dụ doanh thu định kỳ ([[mrr-arr|MRR]]) với phần mềm, giá trị giao dịch với sàn, đơn hàng lặp lại với bán lẻ.',
          'Đưa cả xu hướng, không chỉ con số hiện tại. "MRR 120 triệu, tăng trung bình 15% mỗi tháng trong 6 tháng" nói nhiều hơn "MRR 120 triệu". Xem [[tang-truong-mom|tăng trưởng theo tháng]].',
          'Ghi rõ định nghĩa. "Người dùng" là người đăng ký, người hoạt động hằng tháng hay người trả tiền?',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Dùng chỉ số cho đẹp: lượt tải, lượt xem, tổng số đăng ký từ trước tới nay. Nhà đầu tư gọi là chỉ số phù phiếm và sẽ hỏi số người còn dùng.',
          'Biểu đồ cộng dồn. Đường cộng dồn luôn đi lên kể cả khi tăng trưởng đã chững lại, và người đọc có kinh nghiệm nhận ra ngay.',
          'Gộp doanh thu một lần với doanh thu định kỳ.',
        ],
      },
    ],
    faq: [
      {
        q: 'Traction là gì trong startup?',
        a: 'Là bằng chứng bằng số cho thấy thị trường đón nhận sản phẩm: doanh thu, khách trả tiền, người dùng hoạt động và tốc độ tăng của chúng.',
      },
      {
        q: 'Chưa có doanh thu thì có traction không?',
        a: 'Có, ở giai đoạn sớm: thư cam kết mua, triển khai thử, danh sách chờ có chất lượng, tỷ lệ người dùng thử quay lại. Miễn là nó đến từ khách hàng thật bên ngoài đội ngũ.',
      },
    ],
  },

  'tai-tro-khong-hoan-lai': {
    capNhat: '2026-10-11',
    lienQuan: ['goi-von-la-gi', 'vuon-uom-khoi-nghiep', 'bootstrapping', 'su-dung-von'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Tài trợ không hoàn lại (grant) là khoản tiền một tổ chức cấp cho doanh nghiệp hoặc dự án mà không lấy cổ phần và không đòi trả lại, đổi lại doanh nghiệp phải dùng tiền đúng mục đích đã đăng ký và báo cáo kết quả.',
          'Nguồn thường gặp ở Việt Nam: chương trình hỗ trợ khởi nghiệp đổi mới sáng tạo của nhà nước và địa phương, quỹ của các tổ chức quốc tế, giải thưởng của cuộc thi khởi nghiệp, chương trình hỗ trợ của các tập đoàn công nghệ.',
        ],
      },
      {
        h: 'Khác gì so với tiền đầu tư',
        bang: {
          cot: ['', 'Tài trợ không hoàn lại', 'Vốn đầu tư'],
          hang: [
            ['Mất cổ phần', 'Không', 'Có'],
            ['Phải trả lại', 'Không, nếu dùng đúng mục đích', 'Không, nhà đầu tư chia sẻ rủi ro'],
            ['Ràng buộc', 'Mục đích sử dụng, báo cáo, đôi khi đối ứng', 'Điều khoản cổ đông, quyền của nhà đầu tư'],
            ['Quy mô thường gặp', 'Vài chục triệu tới vài tỷ', 'Tuỳ vòng, từ vài trăm triệu trở lên'],
            ['Thời gian', 'Theo đợt xét duyệt, thường vài tháng', '3 tới 6 tháng mỗi vòng'],
          ],
        },
      },
      {
        h: 'Khi nào nên dùng',
        p: [
          'Tài trợ hợp nhất khi bạn cần tiền cho một việc cụ thể, đo được: phát triển một công nghệ, thử nghiệm với một nhóm khách hàng, đăng ký sở hữu trí tuệ. Nó kéo dài [[runway-la-gi|runway]] mà không [[pha-loang-co-phan|pha loãng]] cổ phần.',
          'Nó không thay được vốn đầu tư khi cần tăng trưởng nhanh, vì quy mô nhỏ, giải ngân theo tiến độ và thường chỉ chi cho các hạng mục đã duyệt. Xem danh sách chương trình đang mở ở [[/chuong-trinh|trang chương trình và tài trợ]].',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chạy theo mọi chương trình đang mở, dồn cả đội vào viết hồ sơ thay vì làm sản phẩm.',
          'Không đọc kỹ điều kiện đối ứng: nhiều chương trình yêu cầu doanh nghiệp tự bỏ một phần chi phí.',
          'Chi sai hạng mục rồi bị yêu cầu hoàn trả khi quyết toán.',
        ],
      },
    ],
    faq: [
      {
        q: 'Tài trợ không hoàn lại có phải trả lại không?',
        a: 'Không, nếu dùng đúng mục đích và hoàn thành nghĩa vụ báo cáo. Dùng sai mục đích có thể bị thu hồi.',
      },
      {
        q: 'Startup ở Việt Nam tìm tài trợ ở đâu?',
        a: 'Các chương trình hỗ trợ đổi mới sáng tạo của bộ, sở khoa học và công nghệ địa phương, các vườn ươm, cuộc thi khởi nghiệp và tổ chức quốc tế. fundraising.vn tổng hợp các chương trình đang mở ở mục Chương trình.',
      },
    ],
  },

  'cvc-quy-doanh-nghiep': {
    capNhat: '2026-10-11',
    lienQuan: ['quy-dau-tu-mao-hiem', 'khau-vi-dau-tu', 'nha-dau-tu-dan-dat', 'exit-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Quỹ đầu tư doanh nghiệp (CVC, corporate venture capital) là đơn vị đầu tư mạo hiểm thuộc một tập đoàn, dùng tiền của tập đoàn để đầu tư vào startup. Khác với [[quy-dau-tu-mao-hiem|quỹ VC]] thông thường, CVC theo đuổi cả lợi nhuận tài chính lẫn lợi ích chiến lược cho công ty mẹ.',
          'Lợi ích chiến lược có thể là tiếp cận công nghệ mới, mở kênh phân phối, thử nghiệm mô hình kinh doanh hoặc tìm mục tiêu mua lại trong tương lai.',
        ],
      },
      {
        h: 'Điểm cộng và điểm trừ khi nhận vốn CVC',
        li: [
          'Điểm cộng: tiếp cận khách hàng, kênh bán hàng và hạ tầng của tập đoàn; uy tín khi bán cho khách doanh nghiệp; đôi khi là hợp đồng thương mại đi kèm.',
          'Điểm trừ: đối thủ của tập đoàn có thể ngại làm việc với bạn; quyết định đầu tư có thể chậm vì phải qua nhiều cấp; ưu tiên chiến lược của tập đoàn thay đổi thì sự hỗ trợ cũng đổi theo.',
          'Cần đọc kỹ các điều khoản như quyền ưu tiên mua lại công ty, độc quyền hợp tác, quyền tiếp cận thông tin. Chúng có thể giới hạn lựa chọn [[exit-la-gi|exit]] của bạn về sau.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Coi lời hứa hợp tác thương mại là chắc chắn. Nên tách hợp đồng thương mại khỏi thoả thuận đầu tư và ghi rõ cam kết.',
          'Để CVC dẫn vòng ngay từ đầu khi chưa có nhà đầu tư tài chính nào. Nhiều quỹ khác sẽ e ngại tham gia sau.',
        ],
      },
    ],
    faq: [
      {
        q: 'CVC là gì?',
        a: 'Là quỹ đầu tư mạo hiểm thuộc một tập đoàn, đầu tư vào startup vì cả lợi nhuận lẫn lợi ích chiến lược cho tập đoàn.',
      },
      {
        q: 'Nên nhận vốn từ CVC hay VC?',
        a: 'Tuỳ bạn cần gì ngoài tiền. Nếu kênh phân phối của tập đoàn là lợi thế lớn, CVC rất đáng cân nhắc. Nếu cần sự trung lập để bán cho nhiều khách hàng cạnh tranh nhau, VC tài chính thường an toàn hơn.',
      },
    ],
  },

  /* ─── Quy trình gọi vốn ────────────────────────────────────────── */

  'elevator-pitch': {
    capNhat: '2026-10-11',
    lienQuan: ['pitch-deck-la-gi', 'one-pager-teaser', 'email-goi-nha-dau-tu', 'warm-intro'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Elevator pitch là phần giới thiệu doanh nghiệp gói trong khoảng 30 tới 60 giây, đủ ngắn để nói hết trong một chuyến thang máy. Mục tiêu không phải thuyết phục ngay, mà là khiến người nghe muốn hỏi thêm hoặc hẹn gặp.',
        ],
      },
      {
        h: 'Một khung dễ dùng',
        li: [
          'Bạn làm gì, cho ai, trong một câu: "Chúng tôi giúp các phòng khám nha khoa nhỏ giảm lịch hẹn bị huỷ."',
          'Vấn đề đau ở đâu, kèm một con số: "Mỗi phòng khám mất khoảng 15% lịch hẹn mỗi tháng vì khách quên."',
          'Cách bạn giải quyết và vì sao khác: "Nhắc lịch tự động qua Zalo, khách xác nhận hoặc đổi giờ bằng một lần bấm."',
          'Bằng chứng: "40 phòng khám đang trả phí, tỷ lệ huỷ giảm còn một nửa."',
          'Bạn cần gì: "Chúng tôi đang gọi 4 tỷ vòng pre-seed để mở rộng ra Hà Nội."',
        ],
        sau: [
          'Năm câu, đọc khoảng 40 giây. Câu cuối quan trọng: người nghe cần biết bạn muốn gì từ họ.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Mở đầu bằng tầm nhìn quá lớn ("thay đổi ngành y tế Việt Nam") trước khi người nghe biết bạn làm gì.',
          'Dùng thuật ngữ kỹ thuật. Người nghe không cần biết bạn dùng công nghệ gì, cần biết nó giải quyết gì.',
          'Học thuộc rồi đọc như đọc bài. Nên tập tới mức nói tự nhiên và điều chỉnh theo người nghe.',
        ],
      },
    ],
    faq: [
      {
        q: 'Elevator pitch là gì?',
        a: 'Là phần giới thiệu ngắn 30 tới 60 giây về doanh nghiệp, đủ để người nghe hiểu bạn làm gì và muốn tìm hiểu thêm.',
      },
      {
        q: 'Elevator pitch dùng khi nào?',
        a: 'Ở sự kiện, khi được giới thiệu với nhà đầu tư, ở đầu buổi gặp, và làm đoạn mở của [[email-goi-nha-dau-tu|email gửi nhà đầu tư]]. Nó cũng là câu trả lời cho câu hỏi "công ty bạn làm gì?".',
      },
    ],
  },

  'one-pager-teaser': {
    capNhat: '2026-10-11',
    lienQuan: ['pitch-deck-la-gi', 'elevator-pitch', 'email-goi-nha-dau-tu', 'warm-intro'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'One-pager (còn gọi teaser) là bản tóm tắt doanh nghiệp gói trong đúng một trang, dùng để mở lời với nhà đầu tư trước khi gửi [[pitch-deck-la-gi|pitch deck]] đầy đủ. Nó đặc biệt hữu ích khi đi qua [[warm-intro|người giới thiệu]]: họ cần một tài liệu ngắn để chuyển tiếp mà không phải tự giải thích hộ bạn.',
        ],
      },
      {
        h: 'Một trang gồm những gì',
        li: [
          'Tên, một câu mô tả bạn làm gì cho ai.',
          'Vấn đề và giải pháp, mỗi phần hai tới ba dòng.',
          'Ba con số [[traction-la-gi|traction]] quan trọng nhất.',
          'Mô hình kinh doanh: thu tiền ai, bao nhiêu.',
          'Đội ngũ: tên, vai trò, một dòng kinh nghiệm liên quan.',
          'Đang gọi bao nhiêu, dùng vào đâu, liên hệ.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Thu nhỏ cỡ chữ để nhét cả deck vào một trang. Một trang nghĩa là phải chọn, không phải nén.',
          'Thiếu thông tin liên hệ hoặc thiếu con số gọi vốn.',
          'Số liệu khác với deck gửi sau đó.',
        ],
      },
    ],
    faq: [
      {
        q: 'One-pager khác pitch deck thế nào?',
        a: 'One-pager là bản một trang để gây chú ý và chuyển tiếp dễ dàng; pitch deck là bộ 10 tới 15 slide để trình bày đầy đủ. Thường gửi one-pager trước, deck sau khi người nhận quan tâm.',
      },
      {
        q: 'Có bắt buộc phải có one-pager không?',
        a: 'Không. Nhiều nhà đầu tư nhận deck ngay. Nhưng khi nhờ người khác giới thiệu, một bản one-pager giúp việc chuyển tiếp nhanh và đúng ý bạn hơn.',
      },
    ],
  },

  'email-goi-nha-dau-tu': {
    capNhat: '2026-10-11',
    lienQuan: ['warm-intro', 'elevator-pitch', 'one-pager-teaser', 'pitch-deck-la-gi', 'khau-vi-dau-tu'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Email đầu tiên gửi nhà đầu tư là thư giới thiệu ngắn nhằm một mục tiêu duy nhất: có được cuộc gặp. Nó có thể là thư gửi thẳng (cold email) hoặc thư nhờ người quen chuyển tiếp ([[warm-intro|warm intro]]). Thư qua người giới thiệu được trả lời nhiều hơn hẳn, nhưng cấu trúc nội dung giống nhau.',
        ],
      },
      {
        h: 'Cấu trúc một email hiệu quả',
        li: [
          'Tiêu đề nói rõ bạn là ai và điểm mạnh nhất: "Pre-seed: phần mềm nhắc lịch cho phòng khám, 40 khách trả phí".',
          'Câu mở cho thấy bạn chọn họ có lý do: họ từng đầu tư vào lĩnh vực gần, hoặc [[khau-vi-dau-tu|khẩu vị]] của họ đúng giai đoạn của bạn.',
          'Ba tới năm dòng: làm gì, cho ai, bằng chứng, đang gọi bao nhiêu. Gần như chính là [[elevator-pitch|elevator pitch]].',
          'Một lời đề nghị cụ thể: "Anh chị có 20 phút trong tuần tới không?".',
          'Đính kèm [[pitch-deck-la-gi|deck]] dạng PDF hoặc đường link mở được ngay.',
        ],
      },
      {
        h: 'Theo dõi thế nào',
        p: [
          'Không trả lời không có nghĩa là từ chối. Gửi một thư nhắc ngắn sau khoảng một tuần, kèm một cập nhật mới nếu có ("vừa ký thêm 5 khách"). Sau hai lần nhắc không có phản hồi thì nên chuyển sang người khác và giữ liên lạc bằng [[investor-update|thư cập nhật]] định kỳ.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Gửi một nội dung chung cho hàng trăm người, có khi còn sai tên quỹ.',
          'Viết quá dài, kể lại toàn bộ deck trong thân thư.',
          'Gửi cho quỹ không đầu tư ở giai đoạn hoặc ngành của bạn.',
          'Không ghi rõ con số đang gọi.',
        ],
      },
    ],
    faq: [
      {
        q: 'Có nên gửi email lạnh cho nhà đầu tư không?',
        a: 'Có thể, nhưng tỷ lệ phản hồi thấp. Nếu tìm được người quen biết nhà đầu tư để giới thiệu, hãy ưu tiên cách đó. Email lạnh viết riêng, ngắn và đúng đối tượng vẫn có cơ hội.',
      },
      {
        q: 'Email gửi nhà đầu tư nên dài bao nhiêu?',
        a: 'Đọc được trong khoảng một phút trên điện thoại: thường dưới 150 chữ trong thân thư, phần còn lại để deck nói.',
      },
    ],
  },

  'nda-goi-von': {
    capNhat: '2026-10-11',
    lienQuan: ['data-room', 'due-diligence', 'pitch-deck-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'NDA (non-disclosure agreement, thoả thuận bảo mật) là văn bản cam kết không tiết lộ thông tin được chia sẻ. Trong gọi vốn, nhiều founder lần đầu muốn nhà đầu tư ký NDA trước khi xem deck. Thực tế, phần lớn quỹ đầu tư mạo hiểm không ký NDA ở giai đoạn đầu.',
        ],
      },
      {
        h: 'Vì sao quỹ thường không ký',
        li: [
          'Họ xem hàng trăm hồ sơ mỗi năm, nhiều hồ sơ trùng ý tưởng. Ký NDA với từng người tạo rủi ro pháp lý cho họ nếu sau này đầu tư vào một công ty tương tự.',
          'Uy tín là tài sản chính của quỹ. Một quỹ bị biết là làm lộ thông tin của startup sẽ khó có thêm hồ sơ tốt.',
          'Ý tưởng hiếm khi là lợi thế quyết định. Thứ khó sao chép là cách bạn làm và tốc độ bạn làm.',
        ],
      },
      {
        h: 'Khi nào NDA là hợp lý',
        p: [
          'Ở giai đoạn [[due-diligence|thẩm định]], khi bạn mở [[data-room|data room]] với hợp đồng khách hàng, mã nguồn, số liệu chi tiết, việc yêu cầu một thoả thuận bảo mật là bình thường và nhà đầu tư thường chấp nhận. Với đối tác chiến lược hoặc [[cvc-quy-doanh-nghiep|CVC]] có thể là đối thủ, NDA càng nên có. Nội dung NDA nên để luật sư xem.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Đòi NDA trước khi gửi deck lần đầu. Nhiều nhà đầu tư sẽ đơn giản là không đọc.',
          'Đưa bí mật kinh doanh thật sự vào deck gửi rộng. Deck nên nói đủ để gây quan tâm, chi tiết nhạy cảm để dành cho giai đoạn sau.',
        ],
      },
    ],
    faq: [
      {
        q: 'Có nên bắt nhà đầu tư ký NDA không?',
        a: 'Không nên ở bước gửi deck và gặp lần đầu. Hợp lý khi tới giai đoạn thẩm định và chia sẻ tài liệu chi tiết.',
      },
    ],
  },

  /* ─── Công cụ đầu tư và điều khoản ──────────────────────────────── */

  'thoa-thuan-co-dong': {
    capNhat: '2026-10-11',
    lienQuan: ['term-sheet', 'hop-dong-mua-co-phan-spa', 'drag-along-tag-along', 'quyen-uu-tien-mua-rofr', 'dieu-le-cong-ty'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Thoả thuận cổ đông (SHA, shareholders agreement) là hợp đồng giữa các cổ đông của công ty, quy định quyền và nghĩa vụ của họ với nhau: ai được cử người vào hội đồng quản trị, quyết định nào cần ai đồng ý, ai được mua hay bán cổ phần trong trường hợp nào.',
          'Khi nhà đầu tư vào, SHA thường được ký cùng lúc với [[hop-dong-mua-co-phan-spa|hợp đồng mua cổ phần (SPA)]]. SPA lo phần mua bán: giá, số lượng, điều kiện giải ngân. SHA lo phần sống chung sau đó.',
        ],
      },
      {
        h: 'Những điều khoản hay gặp',
        li: [
          'Quản trị: số [[ghe-hoi-dong-quan-tri|ghế hội đồng quản trị]], [[quyen-phu-quyet|quyền phủ quyết]] với các vấn đề quan trọng.',
          'Chuyển nhượng cổ phần: [[quyen-uu-tien-mua-rofr|quyền ưu tiên mua]], [[drag-along-tag-along|drag-along và tag-along]], thời gian hạn chế bán.',
          'Phát hành mới: [[quyen-mua-them|quyền mua thêm]] để giữ tỷ lệ, [[chong-pha-loang|chống pha loãng]].',
          'Cam kết của người sáng lập: [[vesting-la-gi|vesting]], không cạnh tranh, làm toàn thời gian.',
          'Quyền thông tin: báo cáo tài chính định kỳ cho nhà đầu tư.',
        ],
      },
      {
        h: 'Lưu ý ở Việt Nam',
        p: [
          'SHA là hợp đồng dân sự giữa các bên, còn [[dieu-le-cong-ty|điều lệ công ty]] là văn bản có giá trị với cả công ty và cơ quan đăng ký. Điều khoản trong SHA trái với Luật Doanh nghiệp hoặc mâu thuẫn với điều lệ có thể khó thực thi. Vì vậy các quyền quan trọng trong SHA thường được đưa vào điều lệ sửa đổi sau vòng gọi vốn. Đây là phần cần luật sư quen giao dịch đầu tư.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chỉ đọc kỹ [[term-sheet|term sheet]] rồi ký SHA mà không đối chiếu. SHA có thể chi tiết hoá điều khoản theo hướng khác với điều đã thống nhất.',
          'Đồng ý danh sách quyền phủ quyết quá rộng, tới mức mọi quyết định vận hành đều cần nhà đầu tư gật đầu.',
          'Không cập nhật điều lệ sau khi ký.',
        ],
      },
    ],
    faq: [
      {
        q: 'SHA là gì?',
        a: 'Là thoả thuận cổ đông: hợp đồng giữa các cổ đông về quyền, nghĩa vụ, cách ra quyết định và chuyển nhượng cổ phần.',
      },
      {
        q: 'SHA và điều lệ công ty khác nhau thế nào?',
        a: 'Điều lệ là văn bản bắt buộc, áp dụng cho công ty và mọi cổ đông, nộp kèm hồ sơ đăng ký doanh nghiệp. SHA là hợp đồng riêng giữa các cổ đông ký tên, chi tiết hơn. Hai văn bản cần khớp nhau.',
      },
    ],
  },

  'hop-dong-mua-co-phan-spa': {
    capNhat: '2026-10-11',
    lienQuan: ['thoa-thuan-co-dong', 'term-sheet', 'due-diligence', 'dong-vong-goi-von', 'von-dieu-le'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Hợp đồng mua cổ phần (SPA, share purchase agreement hoặc share subscription agreement) là hợp đồng ghi nhận việc nhà đầu tư mua cổ phần của công ty: mua bao nhiêu, giá bao nhiêu, trả khi nào, và cần điều kiện gì trước khi tiền được chuyển.',
          'Có hai kiểu: mua cổ phần mới phát hành (tiền vào công ty, [[von-dieu-le|vốn điều lệ]] tăng) và mua lại cổ phần từ cổ đông hiện hữu (tiền vào túi người bán). Gọi vốn cho startup gần như luôn là kiểu thứ nhất.',
        ],
      },
      {
        h: 'Các phần chính',
        li: [
          'Giá, số cổ phần, tổng số tiền, lịch giải ngân (một lần hay nhiều đợt theo cột mốc).',
          'Điều kiện tiên quyết: những việc phải xong trước khi giải ngân, ví dụ sửa điều lệ, ký [[vesting-la-gi|vesting]], chuyển giao [[chuyen-giao-so-huu-tri-tue|sở hữu trí tuệ]], hoàn tất thủ tục đăng ký.',
          'Cam đoan và bảo đảm: công ty và người sáng lập xác nhận thông tin cung cấp là đúng (pháp lý, tài chính, không có tranh chấp ẩn).',
          'Bồi thường: ai chịu trách nhiệm nếu cam đoan sai.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Cam đoan bằng tư cách cá nhân của người sáng lập mà không giới hạn mức trách nhiệm.',
          'Điều kiện tiên quyết quá nhiều hoặc mơ hồ, khiến nhà đầu tư có lý do trì hoãn giải ngân.',
          'Ký xong nhưng không làm thủ tục cập nhật cổ đông và vốn điều lệ, để lệch giữa giấy tờ và thực tế.',
        ],
      },
    ],
    faq: [
      {
        q: 'SPA khác SHA thế nào?',
        a: 'SPA quy định việc mua bán cổ phần: giá, số lượng, điều kiện giải ngân. [[thoa-thuan-co-dong|SHA]] quy định quan hệ giữa các cổ đông sau khi mua. Hai văn bản thường ký cùng lúc.',
      },
    ],
  },

  'drag-along-tag-along': {
    capNhat: '2026-10-11',
    lienQuan: ['thoa-thuan-co-dong', 'quyen-uu-tien-mua-rofr', 'exit-la-gi', 'liquidation-preference'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Đây là hai điều khoản về việc bán công ty, thường nằm trong [[thoa-thuan-co-dong|thoả thuận cổ đông]]:',
        ],
        li: [
          'Drag-along (quyền kéo theo): khi một nhóm cổ đông đủ lớn đồng ý bán công ty, họ có quyền buộc các cổ đông còn lại bán theo cùng điều kiện. Mục đích là để một cổ đông nhỏ không chặn được thương vụ bán toàn bộ công ty.',
          'Tag-along (quyền bán theo): khi một cổ đông lớn bán cổ phần của mình, các cổ đông nhỏ có quyền bán kèm theo cùng giá và điều kiện. Mục đích là để cổ đông nhỏ không bị bỏ lại với một chủ mới họ không chọn.',
        ],
      },
      {
        h: 'Một ví dụ',
        p: [
          'Người sáng lập giữ 60%, quỹ A giữ 25%, ba angel giữ tổng 15%. Một công ty muốn mua lại toàn bộ với giá 200 tỷ. Có drag-along với ngưỡng "trên 70% cổ phần đồng ý", người sáng lập và quỹ A (85%) đồng ý thì ba angel phải bán theo cùng giá.',
          'Ngược lại, nếu người sáng lập bán riêng phần 60% của mình cho một bên thứ ba, nhờ tag-along các angel có quyền yêu cầu bên mua mua cả phần của họ với cùng giá.',
        ],
      },
      {
        h: 'Điểm cần đàm phán',
        li: [
          'Ngưỡng kích hoạt drag-along: tỷ lệ bao nhiêu, có cần cả người sáng lập lẫn nhà đầu tư cùng đồng ý không.',
          'Giá sàn hoặc thời gian tối thiểu trước khi được kéo theo, để tránh bị ép bán quá sớm hoặc quá rẻ.',
          'Cách chia tiền khi bán, vì [[liquidation-preference|quyền ưu tiên thanh toán]] quyết định mỗi người thực nhận bao nhiêu.',
        ],
      },
    ],
    faq: [
      {
        q: 'Drag-along có bất lợi cho người sáng lập không?',
        a: 'Có thể, nếu ngưỡng kích hoạt cho phép nhà đầu tư tự kéo cả người sáng lập vào một thương vụ họ không muốn. Nên đàm phán để việc kích hoạt cần sự đồng ý của cả hai nhóm.',
      },
    ],
  },

  'quyen-uu-tien-mua-rofr': {
    capNhat: '2026-10-11',
    lienQuan: ['thoa-thuan-co-dong', 'drag-along-tag-along', 'quyen-mua-them', 'co-phan-pho-thong'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Quyền ưu tiên mua (ROFR, right of first refusal) là quyền của công ty hoặc cổ đông hiện hữu được mua trước cổ phần mà một cổ đông khác định bán cho người ngoài, với cùng giá và điều kiện người ngoài đưa ra.',
          'Mục đích là để nhóm cổ đông hiện tại kiểm soát được ai bước vào công ty, tránh cổ phần rơi vào tay đối thủ hoặc người không phù hợp.',
        ],
      },
      {
        h: 'Một ví dụ',
        p: [
          'Một angel giữ 5% muốn bán cho một nhà đầu tư mới với giá 3 tỷ. Theo ROFR, angel phải thông báo cho công ty và các cổ đông có quyền. Trong thời hạn thoả thuận, ví dụ 30 ngày, họ có quyền mua 5% đó với đúng giá 3 tỷ. Nếu không ai mua, angel mới được bán cho người ngoài, và không được bán với giá thấp hơn.',
        ],
      },
      {
        h: 'Liên hệ với luật Việt Nam',
        p: [
          'Luật Doanh nghiệp đã có một số cơ chế tương tự: ở công ty TNHH hai thành viên trở lên, thành viên muốn chuyển nhượng vốn phải chào bán cho các thành viên còn lại trước; ở công ty cổ phần, cổ phần phổ thông của cổ đông sáng lập bị hạn chế chuyển nhượng trong 3 năm đầu kể từ ngày được cấp giấy chứng nhận đăng ký doanh nghiệp. ROFR trong [[thoa-thuan-co-dong|thoả thuận cổ đông]] mở rộng hoặc chi tiết hoá thêm, và nên được ghi vào điều lệ. Chi tiết áp dụng nên kiểm tra với luật sư theo quy định hiện hành.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Không quy định thời hạn trả lời, khiến việc chuyển nhượng treo vô thời hạn.',
          'Nhầm ROFR với [[quyen-mua-them|quyền mua thêm]]. ROFR áp dụng khi cổ đông bán cổ phần cũ; quyền mua thêm áp dụng khi công ty phát hành cổ phần mới.',
        ],
      },
    ],
    faq: [
      {
        q: 'ROFR là gì?',
        a: 'Là quyền được mua trước, cùng giá và điều kiện, khi một cổ đông định bán cổ phần cho người ngoài.',
      },
    ],
  },

  /* ─── Cổ phần ──────────────────────────────────────────────────── */

  'vesting-tang-toc': {
    capNhat: '2026-10-11',
    lienQuan: ['vesting-la-gi', 'cliff-vesting', 'good-leaver-bad-leaver', 'exit-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Tăng tốc vesting (acceleration) là điều khoản cho phép phần cổ phần chưa [[vesting-la-gi|vesting]] được vesting sớm, toàn bộ hoặc một phần, khi xảy ra một sự kiện nhất định, thường là khi công ty được bán.',
          'Lý do: nếu công ty bị mua khi bạn mới đi được nửa lịch vesting, bên mua có thể cho bạn nghỉ việc, và phần chưa vesting mất trắng dù bạn đã góp công đưa công ty tới thương vụ đó.',
        ],
      },
      {
        h: 'Single trigger và double trigger',
        bang: {
          cot: ['Loại', 'Khi nào kích hoạt', 'Nhà đầu tư thường thấy'],
          hang: [
            ['Single trigger', 'Chỉ cần công ty được bán', 'Ít ưa: bên mua lo người chủ chốt nhận đủ cổ phần rồi rời đi ngay'],
            ['Double trigger', 'Công ty được bán và người đó bị cho nghỉ không vì lỗi của họ trong một thời gian sau đó', 'Chấp nhận được, phổ biến hơn'],
          ],
        },
        sau: [
          'Ví dụ: người sáng lập có 400.000 cổ phần, đã vesting 200.000 khi công ty được bán. Với double trigger tăng tốc toàn bộ, nếu bị cho nghỉ trong 12 tháng sau thương vụ, 200.000 cổ phần còn lại được vesting ngay.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Đòi single trigger toàn bộ ngay vòng đầu. Nhà đầu tư và bên mua sau này sẽ coi đó là rủi ro.',
          'Quên định nghĩa "cho nghỉ không vì lỗi". Định nghĩa này quyết định điều khoản có tác dụng hay không.',
        ],
      },
    ],
    faq: [
      {
        q: 'Double trigger acceleration là gì?',
        a: 'Là tăng tốc vesting chỉ khi có đủ hai điều kiện: công ty được bán, và người đó bị cho nghỉ không vì lỗi của họ trong một khoảng thời gian sau thương vụ.',
      },
    ],
  },

  'good-leaver-bad-leaver': {
    capNhat: '2026-10-11',
    lienQuan: ['vesting-la-gi', 'co-phan-chet', 'thoa-thuan-dong-sang-lap', 'vesting-tang-toc'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Good leaver và bad leaver là cách phân loại người sáng lập hoặc nhân sự chủ chốt khi họ rời công ty, để quyết định họ được giữ lại bao nhiêu cổ phần và phần còn lại được mua lại với giá nào.',
          'Good leaver thường là người rời đi vì lý do chính đáng: sức khoẻ, hoàn cảnh gia đình, bị cho nghỉ không vì lỗi. Bad leaver là người vi phạm nghiêm trọng, sang làm cho đối thủ, hoặc tự ý bỏ đi trong thời gian cam kết.',
        ],
      },
      {
        h: 'Hai cách xử lý khác nhau',
        bang: {
          cot: ['', 'Good leaver', 'Bad leaver'],
          hang: [
            ['Phần đã vesting', 'Giữ lại, hoặc bán lại theo giá thị trường', 'Có thể phải bán lại theo giá thấp, thường là mệnh giá'],
            ['Phần chưa vesting', 'Công ty mua lại theo giá thấp đã thoả thuận', 'Công ty mua lại theo giá thấp đã thoả thuận'],
          ],
        },
        sau: [
          'Ví dụ: một đồng sáng lập có 300.000 cổ phần, đã vesting 150.000, giá vòng gần nhất là 24.000 đồng một cổ phần. Nếu là good leaver, họ giữ 150.000 cổ phần (tương đương khoảng 3,6 tỷ theo giá vòng gần nhất). Nếu là bad leaver vì sang làm cho đối thủ, thoả thuận có thể buộc họ bán lại cả 150.000 cổ phần theo mệnh giá 10.000 đồng, tức 1,5 tỷ.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Định nghĩa bad leaver quá rộng, khiến mọi trường hợp rời đi đều bị phạt. Người sáng lập sẽ không chấp nhận, hoặc cảm thấy bị đối xử bất công.',
          'Không có định nghĩa nào, tới khi có người rời đi mới tranh luận họ thuộc loại nào.',
          'Áp dụng điều khoản mua lại mà điều lệ không cho phép, khiến việc thực thi gặp khó.',
        ],
      },
    ],
    faq: [
      {
        q: 'Vì sao cần phân biệt good leaver và bad leaver?',
        a: 'Để đối xử công bằng: người rời đi vì lý do chính đáng vẫn được ghi nhận phần đã đóng góp, còn người gây hại cho công ty không hưởng lợi từ việc đó. Nó cũng giúp hạn chế [[co-phan-chet|cổ phần chết]].',
      },
    ],
  },

  /* ─── Định giá ─────────────────────────────────────────────────── */

  'phuong-phap-vc': {
    capNhat: '2026-10-11',
    lienQuan: ['dinh-gia-startup', 'pre-money-post-money', 'phuong-phap-berkus', 'phuong-phap-scorecard', 'exit-la-gi', 'phan-ky-dinh-gia'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Phương pháp VC (VC method) định giá startup bằng cách đi ngược từ giá trị kỳ vọng khi [[exit-la-gi|exit]]: ước lượng công ty đáng giá bao nhiêu sau 5 tới 7 năm, chia cho mức lợi nhuận nhà đầu tư cần đạt, rồi điều chỉnh cho phần bị pha loãng ở các vòng sau.',
          'Đây là cách nhiều quỹ đầu tư mạo hiểm thực sự nghĩ trong đầu, nên hiểu nó giúp bạn đoán trước họ sẽ phản ứng thế nào với định giá bạn đưa ra.',
        ],
      },
      {
        h: 'Tính bằng số cụ thể',
        li: [
          'Bước 1, giá trị khi exit: dự kiến năm thứ 6 doanh thu 200 tỷ, công ty cùng ngành được mua với bội số khoảng 3 lần doanh thu. Giá trị exit khoảng 600 tỷ.',
          'Bước 2, mức lợi nhuận mục tiêu: quỹ ở giai đoạn sớm thường cần khả năng thu về 10 tới 30 lần trên mỗi khoản, vì phần lớn khoản sẽ thua. Lấy 20 lần.',
          'Bước 3, định giá post-money hôm nay: 600 tỷ chia 20 bằng 30 tỷ.',
          'Bước 4, điều chỉnh pha loãng: nếu các vòng sau dự kiến pha loãng tổng cộng 40%, phần sở hữu quỹ mua hôm nay chỉ còn 60% khi exit. Định giá post-money điều chỉnh: 30 tỷ nhân 0,6 bằng 18 tỷ.',
        ],
        sau: [
          'Nếu quỹ bỏ 3 tỷ, [[pre-money-post-money|định giá pre-money]] là 18 tỷ trừ 3 tỷ, tức 15 tỷ, và quỹ sở hữu 3 chia 18, khoảng 16,7%.',
        ],
      },
      {
        h: 'Điểm mạnh và điểm yếu',
        p: [
          'Điểm mạnh: buộc cả hai bên nói về cùng một câu hỏi, công ty này có thể lớn tới đâu. Điểm yếu: kết quả rất nhạy với giả định. Đổi bội số exit từ 3 lên 5 lần là định giá tăng gần gấp đôi. Vì vậy nó nên dùng cùng các cách khác như [[phuong-phap-scorecard|chấm điểm]] hay [[phuong-phap-berkus|Berkus]], không đứng một mình.',
        ],
      },
    ],
    faq: [
      {
        q: 'Phương pháp VC là gì?',
        a: 'Là cách định giá đi ngược từ giá trị kỳ vọng khi exit, chia cho mức lợi nhuận nhà đầu tư cần, rồi trừ phần pha loãng ở các vòng sau.',
      },
      {
        q: 'Vì sao quỹ cần lợi nhuận 10 tới 30 lần?',
        a: 'Vì ở giai đoạn sớm phần lớn khoản đầu tư không thu hồi được vốn. Một vài khoản thắng lớn phải bù cho cả danh mục.',
      },
    ],
  },

  'phuong-phap-berkus': {
    capNhat: '2026-10-11',
    lienQuan: ['dinh-gia-startup', 'phuong-phap-scorecard', 'phuong-phap-vc', 'dinh-gia-qua-cao'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Phương pháp Berkus định giá startup chưa có doanh thu bằng cách cộng giá trị cho năm yếu tố giảm rủi ro, mỗi yếu tố được gán tối đa một mức tiền nhất định. Nó do nhà đầu tư thiên thần Dave Berkus đề xuất, dành cho giai đoạn mà dự báo tài chính gần như chưa có ý nghĩa.',
        ],
      },
      {
        h: 'Năm yếu tố',
        bang: {
          chuThich: 'Mức trần ví dụ, quy đổi và hạ xuống cho bối cảnh Việt Nam',
          cot: ['Yếu tố', 'Giảm rủi ro gì', 'Tối đa (ví dụ)'],
          hang: [
            ['Ý tưởng có giá trị', 'Rủi ro sản phẩm không ai cần', '3 tỷ'],
            ['Có bản mẫu hoặc MVP', 'Rủi ro công nghệ', '3 tỷ'],
            ['Đội ngũ quản lý tốt', 'Rủi ro thực thi', '3 tỷ'],
            ['Quan hệ chiến lược', 'Rủi ro thị trường, cạnh tranh', '3 tỷ'],
            ['Đã ra mắt hoặc có doanh số đầu tiên', 'Rủi ro sản xuất, bán hàng', '3 tỷ'],
          ],
        },
        sau: [
          'Ví dụ: ý tưởng tốt (2 tỷ), đã có MVP chạy ổn (2,5 tỷ), đội ngũ có kinh nghiệm ngành (2,5 tỷ), chưa có đối tác chiến lược (0,5 tỷ), có vài khách trả tiền đầu tiên (1 tỷ). Tổng định giá pre-money khoảng 8,5 tỷ.',
          'Bản gốc dùng mức trần tính bằng đô la Mỹ cho thị trường Mỹ. Khi áp dụng ở Việt Nam, mức trần cần hạ xuống theo mặt bằng giao dịch trong nước; con số trong bảng chỉ để minh hoạ.',
        ],
      },
      {
        h: 'Khi nào dùng',
        p: [
          'Hợp với vòng angel và [[vong-pre-seed|pre-seed]] khi chưa có doanh thu để dùng [[boi-so-doanh-thu|bội số doanh thu]]. Nó đơn giản và minh bạch, nhưng chủ quan: hai người chấm cùng một startup có thể ra hai con số khác nhau. Dùng nó như một mốc tham chiếu bên cạnh [[phuong-phap-scorecard|phương pháp chấm điểm]] và mặt bằng giao dịch thực tế.',
        ],
      },
    ],
    faq: [
      {
        q: 'Phương pháp Berkus có dùng được cho startup đã có doanh thu không?',
        a: 'Không phù hợp lắm. Khi đã có doanh thu đáng kể, các cách dựa trên doanh thu và tăng trưởng phản ánh giá trị tốt hơn.',
      },
    ],
  },

  'dinh-gia-dcf': {
    capNhat: '2026-10-11',
    lienQuan: ['dinh-gia-startup', 'phuong-phap-vc', 'boi-so-doanh-thu', 'mo-hinh-tai-chinh', 'ebitda-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'DCF (discounted cash flow, chiết khấu dòng tiền) định giá doanh nghiệp bằng cách ước lượng dòng tiền tự do trong tương lai, rồi quy về giá trị hôm nay bằng một tỷ lệ chiết khấu phản ánh rủi ro. Tiền nhận được càng xa và càng rủi ro thì hôm nay càng đáng ít.',
        ],
      },
      {
        h: 'Tính bằng số cụ thể',
        p: [
          'Một công ty dự kiến tạo ra dòng tiền tự do 2 tỷ năm thứ nhất, 4 tỷ năm thứ hai, 6 tỷ năm thứ ba. Với tỷ lệ chiết khấu 30% (mức rủi ro cao của startup), giá trị hiện tại của ba khoản lần lượt khoảng 1,54 tỷ, 2,37 tỷ và 2,73 tỷ, cộng lại khoảng 6,6 tỷ. Phần lớn giá trị còn lại thường nằm ở "giá trị cuối kỳ" sau năm thứ ba, tính bằng một giả định tăng trưởng dài hạn.',
          'Cách tính mỗi năm: dòng tiền chia cho (1 cộng tỷ lệ chiết khấu) mũ số năm. Ví dụ năm thứ hai: 4 tỷ chia 1,3 mũ 2, tức 4 chia 1,69, khoảng 2,37 tỷ.',
        ],
      },
      {
        h: 'Vì sao ít dùng cho startup giai đoạn sớm',
        li: [
          'Dòng tiền những năm đầu thường âm, và gần như toàn bộ giá trị nằm ở giả định xa trong tương lai.',
          'Đổi tỷ lệ chiết khấu hoặc tốc độ tăng trưởng vài điểm phần trăm là kết quả thay đổi rất lớn.',
          'Nhà đầu tư giai đoạn sớm thường dùng [[phuong-phap-vc|phương pháp VC]], [[phuong-phap-scorecard|chấm điểm]] hoặc mặt bằng giao dịch tương tự.',
        ],
        sau: [
          'DCF hữu ích hơn khi doanh nghiệp đã có lãi ổn định, hoặc dùng để kiểm tra xem các giả định trong [[mo-hinh-tai-chinh|mô hình tài chính]] có hợp lý không. Bài [[/playbook/dinh-gia-startup-dcf-va-cac-phuong-phap|định giá startup bằng DCF và các phương pháp khác]] có hướng dẫn chi tiết kèm mẫu.',
        ],
      },
    ],
    faq: [
      {
        q: 'DCF là gì?',
        a: 'Là phương pháp định giá bằng cách quy dòng tiền dự kiến trong tương lai về giá trị hôm nay theo một tỷ lệ chiết khấu phản ánh rủi ro.',
      },
      {
        q: 'Startup chưa có lãi có định giá bằng DCF được không?',
        a: 'Tính được, nhưng kết quả phụ thuộc gần như hoàn toàn vào giả định nên ít được tin cậy. Thường chỉ dùng như một góc nhìn tham khảo.',
      },
    ],
  },

  /* ─── Tài chính và chỉ số ──────────────────────────────────────── */

  'tang-truong-mom': {
    capNhat: '2026-10-11',
    lienQuan: ['traction-la-gi', 'mrr-arr', 'churn-rate', 'cohort-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Tăng trưởng theo tháng (MoM, month over month) là phần trăm thay đổi của một chỉ số so với tháng trước, thường là doanh thu hoặc người dùng hoạt động. CMGR (compound monthly growth rate) là tốc độ tăng trưởng kép bình quân mỗi tháng trong một giai đoạn, giúp làm phẳng những tháng tăng đột biến hay sụt giảm.',
        ],
      },
      {
        h: 'Tính bằng số cụ thể',
        p: [
          'Doanh thu tháng 5 là 100 triệu, tháng 6 là 112 triệu. Tăng trưởng MoM là (112 trừ 100) chia 100, tức 12%.',
          'Doanh thu tháng 1 là 50 triệu, tháng 7 là 120 triệu, tức qua 6 tháng tăng gấp 2,4 lần. CMGR bằng 2,4 mũ (1 chia 6) trừ 1, khoảng 15,7% mỗi tháng. Đây là con số nên đưa vào deck thay vì tháng tăng tốt nhất.',
        ],
      },
      {
        h: 'Bao nhiêu là tốt',
        p: [
          'Không có chuẩn chung cho mọi ngành và mọi quy mô. Với startup giai đoạn sớm có doanh thu định kỳ, tăng trưởng đều khoảng 10 tới 20% mỗi tháng thường được coi là tốt; khi quy mô lớn dần, tốc độ tự nhiên chậm lại. Quan trọng hơn con số tuyệt đối là sự đều đặn và nguồn gốc của tăng trưởng.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chọn tháng đẹp nhất để trình bày. Nhà đầu tư sẽ hỏi chuỗi số đầy đủ.',
          'Tính tăng trưởng trên số cộng dồn thay vì số của từng tháng.',
          'Tăng trưởng nhờ khuyến mãi lớn rồi sụt lại. Nên ghi chú những tháng có chiến dịch đặc biệt.',
        ],
      },
    ],
    faq: [
      {
        q: 'MoM là gì?',
        a: 'Là tăng trưởng so với tháng trước, tính bằng (số tháng này trừ số tháng trước) chia số tháng trước.',
      },
      {
        q: 'CMGR khác MoM thế nào?',
        a: 'MoM là tăng trưởng của một tháng. CMGR là tốc độ bình quân kép qua nhiều tháng, ổn định hơn và khó "làm đẹp" hơn.',
      },
    ],
  },

  'nrr-net-revenue-retention': {
    capNhat: '2026-10-11',
    lienQuan: ['churn-rate', 'mrr-arr', 'cohort-la-gi', 'ltv-la-gi', 'product-market-fit'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'NRR (net revenue retention, tỷ lệ giữ chân doanh thu ròng) đo xem doanh thu từ một nhóm khách hàng hiện có thay đổi thế nào sau một khoảng thời gian, sau khi tính cả phần khách mua thêm, khách giảm gói và khách rời đi. Nó không tính doanh thu từ khách mới.',
          'NRR trên 100% nghĩa là kể cả không có khách mới, doanh thu vẫn tăng. Với doanh nghiệp phần mềm bán theo gói, đây là một trong những chỉ số nhà đầu tư xem kỹ nhất.',
        ],
      },
      {
        h: 'Tính bằng số cụ thể',
        p: [
          'Đầu năm, nhóm khách hiện có tạo ra [[mrr-arr|MRR]] 200 triệu. Trong năm, một số khách nâng gói thêm 40 triệu, một số giảm gói bớt 10 triệu, một số rời đi làm mất 20 triệu.',
          'NRR bằng (200 cộng 40 trừ 10 trừ 20) chia 200, tức 210 chia 200, bằng 105%.',
        ],
      },
      {
        h: 'Đọc con số thế nào',
        li: [
          'Dưới 100%: doanh thu từ khách cũ đang co lại, phải liên tục thu hút khách mới chỉ để đứng yên.',
          'Khoảng 100%: giữ được khách nhưng chưa bán thêm được.',
          'Trên 100%: khách cũ chi tiêu nhiều hơn theo thời gian, tăng trưởng có nền vững.',
        ],
        sau: [
          'Với khách doanh nghiệp vừa và nhỏ, NRR thường thấp hơn khách doanh nghiệp lớn vì tỷ lệ rời bỏ cao hơn. So sánh với doanh nghiệp cùng phân khúc khách hàng, không so với con số của các công ty phần mềm lớn nước ngoài.',
        ],
      },
    ],
    faq: [
      {
        q: 'NRR khác churn thế nào?',
        a: '[[churn-rate|Churn]] chỉ đo phần mất đi. NRR tính cả phần mất đi lẫn phần khách hiện có mua thêm, nên phản ánh đầy đủ hơn sức khoẻ của tập khách hàng.',
      },
    ],
  },

  'gmv-take-rate': {
    capNhat: '2026-10-11',
    lienQuan: ['kinh-te-don-vi', 'bien-loi-nhuan-gop', 'traction-la-gi', 'boi-so-doanh-thu'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'GMV (gross merchandise value, tổng giá trị giao dịch) là tổng giá trị hàng hoá hoặc dịch vụ được mua bán qua nền tảng của bạn. Take rate là phần trăm GMV mà nền tảng giữ lại làm doanh thu, qua phí hoa hồng, phí giao dịch hay phí dịch vụ.',
          'Hai chỉ số này quan trọng với mô hình sàn giao dịch, nền tảng kết nối người mua người bán, giao đồ ăn, đặt dịch vụ.',
        ],
      },
      {
        h: 'Tính bằng số cụ thể',
        p: [
          'Trong tháng, người mua đặt hàng qua sàn tổng cộng 5 tỷ đồng. Sàn thu phí 8% trên mỗi đơn. GMV là 5 tỷ, take rate 8%, doanh thu của sàn là 400 triệu.',
          'Khi trình bày với nhà đầu tư, phải tách rõ hai con số này. Ghi "doanh thu 5 tỷ" trong khi doanh thu thật là 400 triệu là lỗi làm mất niềm tin ngay lập tức.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Dùng GMV thay cho doanh thu trong slide tài chính.',
          'Tăng GMV bằng trợ giá lớn. GMV tăng nhưng mỗi đơn lỗ thêm, [[kinh-te-don-vi|kinh tế đơn vị]] xấu đi.',
          'Không tính phần hoàn, huỷ đơn. Nên trình bày GMV ròng sau hoàn huỷ.',
        ],
      },
    ],
    faq: [
      {
        q: 'GMV có phải doanh thu không?',
        a: 'Không. GMV là tổng giá trị giao dịch đi qua nền tảng. Doanh thu của nền tảng chỉ là phần giữ lại, bằng GMV nhân take rate.',
      },
      {
        q: 'Take rate bao nhiêu là hợp lý?',
        a: 'Tuỳ ngành: sàn hàng hoá thường thấp hơn nền tảng dịch vụ. Quan trọng là take rate có giữ được khi cạnh tranh tăng và có đủ bù chi phí vận hành mỗi đơn hay không.',
      },
    ],
  },

  'ebitda-la-gi': {
    capNhat: '2026-10-11',
    lienQuan: ['bien-loi-nhuan-gop', 'diem-hoa-von', 'burn-rate', 'dinh-gia-dcf'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'EBITDA là lợi nhuận trước lãi vay, thuế và khấu hao (earnings before interest, taxes, depreciation and amortization). Nó cho biết hoạt động kinh doanh cốt lõi tạo ra bao nhiêu tiền, trước khi tính tới cách công ty được tài trợ (vay hay vốn), chính sách thuế và chi phí khấu hao tài sản.',
        ],
      },
      {
        h: 'Tính bằng số cụ thể',
        p: [
          'Trong năm, công ty có lợi nhuận sau thuế 1 tỷ, đã trả lãi vay 300 triệu, thuế thu nhập doanh nghiệp 250 triệu, khấu hao máy móc và phần mềm 450 triệu. EBITDA bằng 1 cộng 0,3 cộng 0,25 cộng 0,45, tức 2 tỷ.',
        ],
      },
      {
        h: 'Startup có cần quan tâm không',
        p: [
          'Ở giai đoạn sớm, EBITDA thường âm và nhà đầu tư nhìn vào [[burn-rate|mức đốt tiền]], [[bien-loi-nhuan-gop|biên lợi nhuận gộp]] và tăng trưởng hơn. EBITDA quan trọng dần khi doanh nghiệp tiến gần [[diem-hoa-von|điểm hoà vốn]], và là cơ sở định giá phổ biến khi doanh nghiệp đã có lãi ổn định hoặc trong các thương vụ mua bán.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Coi EBITDA dương là có tiền. Nó chưa trừ khoản đầu tư tài sản, thay đổi công nợ hay lãi vay, nên công ty vẫn có thể thiếu tiền mặt.',
          'Tự thêm quá nhiều khoản "điều chỉnh" để EBITDA trông đẹp hơn. Nhà đầu tư sẽ tính lại.',
        ],
      },
    ],
    faq: [
      {
        q: 'EBITDA khác lợi nhuận ròng thế nào?',
        a: 'Lợi nhuận ròng đã trừ hết lãi vay, thuế và khấu hao. EBITDA cộng ba khoản đó trở lại để nhìn riêng hiệu quả kinh doanh cốt lõi.',
      },
    ],
  },

  /* ─── Pháp lý Việt Nam ─────────────────────────────────────────── */

  'von-dieu-le': {
    capNhat: '2026-10-11',
    lienQuan: ['cong-ty-co-phan-goi-von', 'loai-hinh-doanh-nghiep-startup', 'bang-co-phan', 'hop-dong-mua-co-phan-spa', 'dieu-le-cong-ty'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Vốn điều lệ là tổng số vốn các thành viên hoặc cổ đông đã góp hoặc cam kết góp khi thành lập công ty, ghi trong [[dieu-le-cong-ty|điều lệ]] và giấy chứng nhận đăng ký doanh nghiệp. Với công ty cổ phần, đó là tổng mệnh giá cổ phần đã bán.',
          'Các thành viên, cổ đông chịu trách nhiệm về nghĩa vụ của công ty trong phạm vi số vốn đã cam kết góp. Theo Luật Doanh nghiệp hiện hành, vốn cam kết phải góp đủ trong thời hạn 90 ngày kể từ ngày được cấp giấy chứng nhận đăng ký doanh nghiệp; nên kiểm tra quy định mới nhất trước khi đăng ký.',
        ],
      },
      {
        h: 'Vốn điều lệ thay đổi thế nào khi gọi vốn',
        p: [
          'Ở công ty cổ phần, mệnh giá cổ phần thường là 10.000 đồng. Khi nhà đầu tư mua cổ phần mới với giá cao hơn mệnh giá, chỉ phần mệnh giá làm tăng vốn điều lệ, phần chênh lệch ghi vào thặng dư vốn cổ phần.',
          'Ví dụ: nhà đầu tư bỏ 6 tỷ mua 250.000 cổ phần mới, giá 24.000 đồng một cổ phần. Vốn điều lệ tăng 250.000 nhân 10.000, tức 2,5 tỷ. Phần còn lại 3,5 tỷ là thặng dư vốn cổ phần. Cả 6 tỷ đều là tiền của công ty; cách hạch toán chỉ khác nhau ở tên tài khoản. Sau đó công ty làm thủ tục thay đổi vốn điều lệ với cơ quan đăng ký kinh doanh.',
        ],
      },
      {
        h: 'Nên đăng ký vốn điều lệ bao nhiêu',
        li: [
          'Đủ cho nhu cầu hoạt động ban đầu và phù hợp khả năng góp thật của người sáng lập. Không có mức tối thiểu chung, trừ một số ngành nghề kinh doanh có điều kiện yêu cầu vốn pháp định.',
          'Đăng ký quá cao rồi không góp đủ đúng hạn sẽ phải làm thủ tục giảm vốn, và để lại dấu vết trong hồ sơ mà nhà đầu tư sẽ thấy khi [[due-diligence|thẩm định]].',
          'Một số đối tác, gói thầu hay thủ tục cấp phép xem xét vốn điều lệ như một tiêu chí năng lực, nên cân nhắc nếu bạn bán cho khách doanh nghiệp lớn.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Khai vốn "ảo" cho đẹp hồ sơ mà không góp thật.',
          'Góp vốn bằng tiền mặt không có chứng từ, hoặc góp qua tài khoản cá nhân. Nên chuyển khoản vào tài khoản công ty, ghi rõ nội dung góp vốn.',
          'Bảng cổ phần nội bộ không khớp với vốn điều lệ và danh sách cổ đông đã đăng ký.',
        ],
      },
    ],
    faq: [
      {
        q: 'Vốn điều lệ là gì?',
        a: 'Là tổng số vốn thành viên, cổ đông đã góp hoặc cam kết góp vào công ty, ghi trong điều lệ và đăng ký doanh nghiệp. Họ chịu trách nhiệm trong phạm vi số vốn này.',
      },
      {
        q: 'Startup nên để vốn điều lệ bao nhiêu?',
        a: 'Đủ cho nhu cầu ban đầu và đúng khả năng góp thật. Khi gọi vốn, vốn điều lệ sẽ tăng theo số cổ phần mới phát hành, nên không cần đăng ký cao ngay từ đầu.',
      },
    ],
  },

  'cau-truc-holding-singapore': {
    capNhat: '2026-10-11',
    lienQuan: ['cong-ty-co-phan-goi-von', 'loai-hinh-doanh-nghiep-startup', 'safe-la-gi', 'esop-la-gi', 'quy-dau-tu-mao-hiem'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Cấu trúc holding ở Singapore (hay được gọi là "flip") là việc người sáng lập lập một công ty mẹ tại Singapore, nắm giữ công ty hoạt động tại Việt Nam. Nhà đầu tư rót vốn và nhận cổ phần ở công ty Singapore thay vì công ty Việt Nam.',
          'Nhiều startup Việt Nam gọi vốn từ quỹ khu vực hoặc quốc tế đi theo hướng này, thường từ vòng [[vong-seed|seed]] trở đi. Nó không bắt buộc, và với nhiều startup ở giai đoạn đầu chưa cần thiết.',
        ],
      },
      {
        h: 'Vì sao nhà đầu tư nước ngoài hay yêu cầu',
        li: [
          'Khung pháp lý quen thuộc: hợp đồng theo thông lệ quốc tế, [[co-phan-uu-dai|cổ phần ưu đãi]], [[safe-la-gi|SAFE]], [[esop-la-gi|ESOP]] triển khai đơn giản hơn.',
          'Chuyển tiền và thoái vốn thuận tiện hơn khi [[exit-la-gi|exit]].',
          'Dễ gọi vốn từ nhiều quỹ khác nhau ở vòng sau, vì cấu trúc này đã phổ biến trong khu vực.',
        ],
      },
      {
        h: 'Cái giá phải trả',
        li: [
          'Chi phí thiết lập và duy trì hằng năm ở Singapore (thư ký công ty, kế toán, kiểm toán theo quy định) cộng thêm chi phí ở Việt Nam. Với startup chưa gọi vốn lớn, khoản này đáng kể.',
          'Thủ tục phía Việt Nam: công ty Việt Nam trở thành doanh nghiệp có vốn đầu tư nước ngoài; phần sở hữu của người sáng lập ở công ty Singapore liên quan tới quy định về đầu tư ra nước ngoài và quản lý ngoại hối. Đây là phần nhiều startup làm sai hoặc bỏ qua, và cần luật sư hai nước.',
          'Chuyển [[chuyen-giao-so-huu-tri-tue|sở hữu trí tuệ]] và hợp đồng khách hàng về đúng pháp nhân, cùng các vấn đề thuế khi chuyển giao.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Lập holding quá sớm chỉ vì "startup nào cũng làm", trong khi nhà đầu tư thực tế là angel và quỹ trong nước.',
          'Làm phía Singapore xong nhưng bỏ dở thủ tục phía Việt Nam, để lại rủi ro pháp lý mà [[due-diligence|thẩm định]] vòng sau sẽ phát hiện.',
          'Không tính trước chi phí duy trì hằng năm vào [[runway-la-gi|runway]].',
        ],
      },
    ],
    faq: [
      {
        q: 'Startup Việt Nam có bắt buộc lập công ty ở Singapore để gọi vốn không?',
        a: 'Không. Nhiều vòng angel và pre-seed, và cả nhiều quỹ trong nước, đầu tư trực tiếp vào công ty cổ phần Việt Nam. Holding Singapore thường được cân nhắc khi nhà đầu tư nước ngoài yêu cầu.',
      },
      {
        q: 'Khi nào nên lập holding Singapore?',
        a: 'Khi có nhà đầu tư cụ thể yêu cầu, hoặc khi kế hoạch gọi vốn và mở rộng khu vực đã rõ. Nên làm với luật sư ở cả hai nước và tính đủ chi phí trước khi quyết định.',
      },
    ],
  },
  /* ─── Bổ sung 19/10/2026: định giá và điều khoản ──────────────── */

  'phan-ky-dinh-gia': {
    capNhat: '2026-10-19',
    lienQuan: ['pha-loang-co-phan', 'phuong-phap-vc', 'exit-la-gi', 'vong-tang-gia', 'bang-co-phan'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Phân kỳ định giá (valuation divergence) là khoảng chênh giữa việc công ty tăng giá trị bao nhiêu lần và cổ phần của nhà đầu tư tăng giá trị bao nhiêu lần, tính từ lúc họ rót vốn tới lúc exit. Hai con số này gần như không bao giờ bằng nhau, vì sau mỗi vòng gọi vốn, phần sở hữu của nhà đầu tư cũ bị [[pha-loang-co-phan|pha loãng]].',
          'Khái niệm do Luis Villalobos, nhà sáng lập mạng lưới Tech Coast Angels, đặt ra. Theo dữ liệu ông tổng hợp, ở các thương vụ thành công, giá trị công ty thường tăng nhanh hơn giá trị cổ phần của nhà đầu tư thiên thần từ 3 tới 5 lần.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Một angel rót 2 tỷ đồng và nhận 10% công ty, tức định giá post-money 20 tỷ. Slide exit của founder viết: "Sáu năm nữa công ty được bán 500 tỷ, anh nhận 50 tỷ, gấp 25 lần."',
          'Thực tế, trong sáu năm đó công ty gọi thêm ba vòng, mỗi vòng bán 20% cổ phần, và mở rộng quỹ cổ phiếu thưởng thêm 10%. Phần của angel còn: 10% × 0,8 × 0,8 × 0,8 × 0,9 = khoảng 4,6%. Bán công ty 500 tỷ, angel nhận khoảng 23 tỷ, tức gấp 11,5 lần chứ không phải 25 lần.',
        ],
        bang: {
          cot: ['', 'Lúc đầu tư', 'Lúc exit', 'Tăng bao nhiêu lần'],
          hang: [
            ['Giá trị công ty', '20 tỷ', '500 tỷ', '25 lần'],
            ['Giá trị cổ phần của angel', '2 tỷ', 'khoảng 23 tỷ', 'khoảng 11,5 lần'],
            ['Phân kỳ', '', '', '25 ÷ 11,5 = khoảng 2,2 lần'],
          ],
        },
        sau: [
          'Ví dụ thật mà Villalobos hay kể là Gadzoox Networks: nhóm angel rót 2 triệu USD cho 30,3% cổ phần. Tới lúc IPO, giá trị công ty tăng 284 lần nhưng giá mỗi cổ phần chỉ tăng 101 lần, vì phần sở hữu của họ đã giảm còn 10,8%. Độ phân kỳ là 2,8 lần, ngay cả ở một thương vụ cực kỳ thành công.',
        ],
      },
      {
        h: 'Vì sao founder cần hiểu khái niệm này',
        p: [
          'Đây là lý do nhà đầu tư giai đoạn sớm nói tới mức lợi nhuận kỳ vọng nghe "tham" như 20 tới 30 lần. Nếu họ cần lãi 5 tới 10 lần thật sự sau khi đã bị pha loãng, thì khi tính bằng [[phuong-phap-vc|phương pháp VC]] trên một bảng cổ phần chưa pha loãng, con số phải nhân thêm 3 tới 5 lần nữa.',
          'Hiểu điều này giúp cuộc đàm phán định giá bớt căng thẳng: hai bên nói cùng một ngôn ngữ, và founder không còn đưa ra lời hứa lợi nhuận mà nhà đầu tư có kinh nghiệm biết ngay là sai.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Tính lợi nhuận cho nhà đầu tư bằng cách nhân tỷ lệ sở hữu hôm nay với giá trị exit. Nhà đầu tư đọc slide đó sẽ kết luận founder chưa từng gọi vốn qua nhiều vòng.',
          '"Ảo tưởng không pha loãng": tin rằng công ty sẽ tự tạo đủ tiền để không phải gọi thêm. Rất ít công ty đạt doanh thu hàng trăm tỷ mà không cần thêm vốn cho đội ngũ, công nợ phải thu và mở rộng.',
          'Quên tính quỹ cổ phiếu thưởng. Mỗi lần mở rộng [[esop-la-gi|ESOP]] cũng pha loãng nhà đầu tư cũ giống như một vòng gọi vốn.',
        ],
      },
    ],
    faq: [
      {
        q: 'Phân kỳ định giá là gì?',
        a: 'Là tỷ lệ giữa mức tăng giá trị của công ty và mức tăng giá trị cổ phần của nhà đầu tư từ lúc đầu tư tới lúc exit. Do bị pha loãng qua các vòng sau, cổ phần của nhà đầu tư sớm thường tăng chậm hơn giá trị công ty từ 3 tới 5 lần.',
      },
      {
        q: 'Làm sao ước lượng phân kỳ cho công ty của mình?',
        a: 'Lập kế hoạch số vòng sẽ gọi tới khi exit, tỷ lệ bán ra mỗi vòng và các lần mở rộng ESOP, rồi nhân dồn các hệ số còn lại. Công cụ mô phỏng vòng gọi vốn làm phép tính này tự động.',
      },
      {
        q: 'Phân kỳ có làm nhà đầu tư thiệt không?',
        a: 'Không hẳn. Pha loãng đi kèm tiền mới giúp công ty lớn lên, nên phần nhỏ hơn của một công ty lớn hơn vẫn có thể đáng giá hơn. Vấn đề chỉ nằm ở chỗ tính lợi nhuận kỳ vọng cho đúng.',
      },
    ],
  },

  'chiet-khau-kho-chuyen-nhuong': {
    capNhat: '2026-10-19',
    lienQuan: ['boi-so-doanh-thu', 'dinh-gia-startup', 'dinh-gia-dcf', 'exit-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Chiết khấu do khó chuyển nhượng (discount for lack of marketability, viết tắt DLOM) là mức giảm giá áp lên giá trị cổ phần của một công ty chưa niêm yết, vì người nắm giữ không thể bán lại nhanh và dễ như cổ phiếu trên sàn.',
          'Cùng một dòng tiền, một cổ phần bán được ngay trong ngày đáng giá hơn một cổ phần phải chờ nhiều năm tới khi có người mua. Chiết khấu này đưa sự khác biệt đó vào con số định giá.',
        ],
      },
      {
        h: 'Khi nào cần dùng',
        p: [
          'Chiết khấu này cần thiết nhất khi bạn định giá startup bằng cách so với công ty đã niêm yết, ví dụ dùng [[boi-so-doanh-thu|bội số doanh thu]] của các công ty công nghệ trên sàn. Giá trên sàn đã bao gồm tính thanh khoản, còn cổ phần startup thì không, nên phải trừ bớt.',
          'Giáo trình định giá của CFA Institute còn tách riêng chiết khấu do thiếu quyền kiểm soát: cổ đông thiểu số không quyết định được chiến lược hay cổ tức, nên phần của họ cũng đáng giá thấp hơn phần của người nắm quyền.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Một startup phần mềm có doanh thu năm 20 tỷ đồng. Các công ty phần mềm tương đương trên sàn giao dịch ở mức 5 lần doanh thu, cho ra 100 tỷ. Áp chiết khấu 30% do khó chuyển nhượng, giá trị dùng để đàm phán còn 70 tỷ.',
          'Trong thực hành, mức chiết khấu thường được đưa ra trong khoảng 20% tới 35%, tuỳ khả năng sớm có một sự kiện thanh khoản như bán công ty hoặc IPO. Công cụ định giá có doanh thu của fundraising.vn gợi ý mặc định 30%.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Lấy bội số của công ty niêm yết áp thẳng cho startup mà không trừ gì. Nhà đầu tư sẽ tự trừ, và còn trừ nhiều hơn bạn nghĩ.',
          'Áp chiết khấu hai lần: vừa dùng tỷ lệ chiết khấu dòng tiền rất cao để phản ánh rủi ro startup, vừa trừ thêm chiết khấu thanh khoản cho cùng một rủi ro.',
          'Giữ nguyên chiết khấu khi sắp có thương vụ bán công ty. Nếu sự kiện thanh khoản gần như chắc chắn, chiết khấu nên nhỏ lại hoặc bỏ đi.',
        ],
      },
    ],
    faq: [
      {
        q: 'DLOM là gì?',
        a: 'Là chiết khấu do khó chuyển nhượng: mức giảm giá áp lên cổ phần công ty chưa niêm yết vì người nắm giữ không bán lại được dễ dàng như cổ phiếu trên sàn.',
      },
      {
        q: 'Chiết khấu bao nhiêu là hợp lý?',
        a: 'Không có con số chuẩn. Các nghiên cứu dựa trên cổ phiếu bị hạn chế chuyển nhượng và giao dịch trước IPO thường cho ra khoảng 20% tới 35%. Quan trọng là giải thích được vì sao bạn chọn mức đó.',
      },
    ],
  },

  'chung-quyen-warrant': {
    capNhat: '2026-10-19',
    lienQuan: ['term-sheet', 'pre-money-post-money', 'pha-loang-toan-phan', 'liquidation-preference', 'co-tuc-tich-luy'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Chứng quyền (warrant) là quyền, không phải nghĩa vụ, được mua thêm một số cổ phần của công ty trong một thời hạn, với giá đã chốt từ trước. Nhà đầu tư thường nhận chứng quyền "tặng kèm" khi rót vốn, hoặc bên cho vay nhận kèm khi cấp khoản vay cho startup.',
          'Chứng quyền là một trong những cách nhà đầu tư lấy lại phần chênh khi chấp nhận mức định giá founder đề xuất: con số định giá trên giấy giữ nguyên, nhưng thực tế nhà đầu tư có quyền sở hữu nhiều hơn.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Founder muốn định giá pre-money 40 tỷ, angel chỉ muốn 30 tỷ. Hai bên chốt 40 tỷ, angel rót 4 tỷ với giá 20.000 đồng một cổ phần, nhận 200.000 cổ phần. Kèm theo là chứng quyền 30%: quyền mua thêm 60.000 cổ phần cùng giá 20.000 đồng trong 5 năm.',
          'Ba năm sau, vòng Series A định giá 60.000 đồng một cổ phần. Angel thực hiện chứng quyền, trả 1,2 tỷ đồng và nhận số cổ phần trị giá 3,6 tỷ theo giá vòng mới. Tính gộp lại, angel đã mua 260.000 cổ phần với tổng 5,2 tỷ, nhiều hơn hẳn những gì định giá 40 tỷ trên giấy cho thấy.',
        ],
      },
      {
        h: 'Những thông số cần đọc kỹ',
        li: [
          'Tỷ lệ phủ: được mua thêm bao nhiêu phần trăm so với số cổ phần đã mua. 5% là một "phần thưởng nhỏ", 100% là thay đổi định giá thật sự.',
          'Giá thực hiện: bằng giá vòng hiện tại, hay thấp hơn.',
          'Thời hạn: 1 năm khác rất xa 10 năm. Thời hạn càng dài, nhà đầu tư càng có thời gian chờ công ty tăng giá rồi mới mua.',
          'Loại cổ phần được mua: [[co-phan-pho-thong|phổ thông]] hay [[co-phan-uu-dai|ưu đãi]].',
        ],
        sau: [
          'Ở Việt Nam, công ty muốn phát hành thêm cổ phần khi nhà đầu tư thực hiện quyền vẫn phải được đại hội đồng cổ đông thông qua theo điều lệ. Vì vậy quyền này thường được ghi trong thoả thuận cổ đông, kèm cam kết của các cổ đông hiện hữu sẽ biểu quyết thông qua. Nên làm với luật sư.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chỉ nhìn con số định giá mà bỏ qua chứng quyền đi kèm. Hai term sheet cùng định giá 40 tỷ có thể khác nhau rất xa.',
          'Quên đưa chứng quyền vào [[bang-co-phan|bảng cổ phần]] theo cách [[pha-loang-toan-phan|pha loãng toàn phần]]. Nhà đầu tư vòng sau sẽ tính, và tỷ lệ của founder thấp hơn bạn tưởng.',
        ],
      },
    ],
    faq: [
      {
        q: 'Chứng quyền khác quyền chọn cổ phiếu của nhân viên thế nào?',
        a: 'Về cơ chế giống nhau: quyền mua cổ phần với giá định trước. Khác ở người nhận: quyền chọn (option) trao cho nhân viên trong chương trình ESOP, còn chứng quyền thường trao cho nhà đầu tư hoặc bên cho vay.',
      },
      {
        q: 'Có nên chấp nhận chứng quyền để giữ được định giá cao?',
        a: 'Đôi khi hợp lý nếu tỷ lệ phủ nhỏ và thời hạn ngắn. Nhưng hãy quy đổi ra định giá thật trước khi quyết định, vì một con số định giá đẹp trên giấy không có nhiều ý nghĩa nếu bạn đã trả lại phần chênh bằng chứng quyền.',
      },
    ],
  },

  'co-tuc-tich-luy': {
    capNhat: '2026-10-19',
    lienQuan: ['liquidation-preference', 'co-phan-uu-dai', 'thac-phan-phoi-thanh-ly', 'chung-quyen-warrant'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Cổ tức tích luỹ là mức cổ tức hằng năm dành cho [[co-phan-uu-dai|cổ phần ưu đãi]], ví dụ 8% số vốn đầu tư, nhưng không trả bằng tiền mỗi năm. Số tiền này được cộng dồn, và nhà đầu tư nhận cùng lúc với [[liquidation-preference|quyền ưu tiên thanh toán]] khi công ty được bán.',
          'Hiệu quả của nó là tăng lợi nhuận của nhà đầu tư mà không thay đổi con số định giá trên giấy. Vì vậy nó là một trong những điều khoản cần quy đổi ra tiền khi so sánh các term sheet.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Quỹ đầu tư 10 tỷ đồng cho 25% cổ phần, có ưu tiên thanh toán 1 lần không tham gia và cổ tức tích luỹ 8% một năm, không tính lãi kép. Năm năm sau công ty được bán với giá 40 tỷ.',
        ],
        bang: {
          cot: ['', 'Không có cổ tức tích luỹ', 'Có cổ tức tích luỹ 8%'],
          hang: [
            ['Quỹ được ưu tiên nhận', '10 tỷ', '10 tỷ + 4 tỷ cổ tức = 14 tỷ'],
            ['Nếu chuyển thành cổ phần phổ thông', '25% × 40 tỷ = 10 tỷ', '25% × 40 tỷ = 10 tỷ'],
            ['Quỹ chọn', '10 tỷ', '14 tỷ'],
            ['Founder và cổ đông khác nhận', '30 tỷ', '26 tỷ'],
          ],
        },
        sau: [
          'Bốn tỷ đồng chuyển từ túi founder sang quỹ, trong khi con số định giá trên term sheet không đổi.',
        ],
      },
      {
        h: 'Phân biệt với cổ tức "khi được công bố"',
        p: [
          'Nhiều term sheet có câu "cổ phần ưu đãi được nhận cổ tức khi hội đồng quản trị công bố". Đây là điều khoản mang tính bảo vệ: thực tế không ai kỳ vọng startup trả cổ tức, và hội đồng quản trị sẽ không công bố. Loại này gần như vô hại. Loại cần đọc kỹ là cổ tức tự động cộng dồn, không cần ai công bố.',
          'Theo các nhà đầu tư thiên thần có kinh nghiệm, kết hợp cổ tức tích luỹ với ưu tiên thanh toán có tham gia là điều khoản bất thường và có thể bị coi là lạm dụng.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Đọc lướt vì nghĩ startup sẽ không bao giờ trả cổ tức. Cổ tức tích luỹ không cần công ty có tiền, nó chỉ chờ tới ngày bán công ty.',
          'Không hỏi lãi kép hay lãi đơn. Với lãi kép 8% trong 7 năm, 10 tỷ sinh ra khoảng 7,1 tỷ cổ tức thay vì 5,6 tỷ.',
        ],
      },
    ],
    faq: [
      {
        q: 'Cổ tức tích luỹ có phải trả hằng năm không?',
        a: 'Thường là không. Nó được cộng dồn và chỉ trả khi có sự kiện thanh khoản như bán công ty, cộng vào số tiền nhà đầu tư được ưu tiên nhận.',
      },
      {
        q: 'Có nên chấp nhận điều khoản này?',
        a: 'Ở vòng sớm, nên đề nghị đổi sang cổ tức "khi được công bố". Nếu nhà đầu tư giữ, hãy đàm phán lãi đơn, mức thấp, và không kết hợp với ưu tiên thanh toán có tham gia.',
      },
    ],
  },

  'thac-phan-phoi-thanh-ly': {
    capNhat: '2026-10-19',
    lienQuan: ['liquidation-preference', 'pari-passu', 'co-phan-uu-dai', 'exit-la-gi', 'co-tuc-tich-luy'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Thác phân phối (liquidation waterfall) là thứ tự chia tiền khi công ty được bán hoặc giải thể. Tiền chảy như nước qua từng bậc: bậc trên nhận đủ thì phần còn lại mới chảy xuống bậc dưới.',
          'Thứ tự thường gặp: trả nợ trước (vay ngân hàng, lương, thuế, nhà cung cấp), sau đó tới [[liquidation-preference|quyền ưu tiên thanh toán]] của cổ phần ưu đãi, cuối cùng phần còn lại chia cho [[co-phan-pho-thong|cổ phần phổ thông]], nơi founder và nhân viên đứng.',
        ],
      },
      {
        h: 'Khi có nhiều vòng: xếp chồng hay ngang hàng',
        p: [
          'Khi công ty đã gọi nhiều vòng, câu hỏi quan trọng là các nhà đầu tư ưu đãi chia với nhau thế nào. Có hai cách. Xếp chồng (stacked): vòng sau nhận đủ trước, rồi mới tới vòng trước. Ngang hàng ([[pari-passu|pari passu]]): mọi vòng cùng chia một lượt, theo tỷ lệ số tiền được ưu tiên.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Công ty gọi seed 10 tỷ đồng và Series A 40 tỷ đồng, cả hai đều có ưu tiên 1 lần không tham gia. Công ty được bán 40 tỷ, trả 5 tỷ nợ, còn 35 tỷ để chia. Tổng số tiền được ưu tiên là 50 tỷ, nhiều hơn số tiền đang có.',
        ],
        bang: {
          cot: ['Bên nhận', 'Xếp chồng (Series A trước)', 'Ngang hàng'],
          hang: [
            ['Chủ nợ', '5 tỷ', '5 tỷ'],
            ['Nhà đầu tư Series A', '35 tỷ', '35 × 40/50 = 28 tỷ'],
            ['Nhà đầu tư seed', '0', '35 × 10/50 = 7 tỷ'],
            ['Founder và nhân viên', '0', '0'],
          ],
        },
        sau: [
          'Ở mức giá bán này, founder không nhận được gì theo cả hai cách. Đó là lý do nên tính thử nhiều mức giá exit: thường phải bán trên tổng số vốn đã gọi một khoảng đáng kể thì cổ phần phổ thông mới bắt đầu có giá trị.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chỉ nhìn tỷ lệ sở hữu. Founder nắm 55% vẫn có thể nhận 0 đồng nếu giá bán thấp hơn tổng ưu tiên cộng nợ.',
          'Không đọc thứ tự giữa các vòng. Nhà đầu tư seed thường không để ý cho tới khi vòng sau đưa ra điều khoản xếp chồng.',
          'Quên các khoản chảy trước cổ phần ưu đãi: thưởng giữ chân ban điều hành khi bán công ty, phí tư vấn thương vụ.',
        ],
      },
    ],
    faq: [
      {
        q: 'Thác phân phối là gì?',
        a: 'Là thứ tự chia tiền khi bán hoặc giải thể công ty: chủ nợ trước, cổ phần ưu đãi theo quyền ưu tiên thanh toán sau, cổ phần phổ thông cuối cùng.',
      },
      {
        q: 'Xếp chồng hay ngang hàng có lợi cho founder hơn?',
        a: 'Với founder, hai cách thường như nhau vì founder đứng ở bậc cuối. Khác biệt lớn nằm giữa các nhóm nhà đầu tư. Nhưng ngang hàng giúp các vòng sau dễ thống nhất hơn, nên thường được khuyên dùng.',
      },
    ],
  },

  'pari-passu': {
    capNhat: '2026-10-19',
    lienQuan: ['thac-phan-phoi-thanh-ly', 'liquidation-preference', 'co-phan-uu-dai', 'term-sheet'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Pari passu là cụm từ tiếng Latin, nghĩa là "cùng bước". Trong hợp đồng đầu tư, nó có nghĩa các bên được đối xử ngang hàng: cùng một thứ tự ưu tiên, chia theo cùng một tỷ lệ, không ai được trả trước ai.',
          'Cụm từ này xuất hiện ở ba chỗ chính trong term sheet: thứ tự ưu tiên thanh toán giữa các vòng gọi vốn, quyền của cổ phần mới so với cổ phần cũ, và thứ tự trả nợ giữa các khoản vay.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Nhà đầu tư seed được ưu tiên 6 tỷ đồng, nhà đầu tư Series A được ưu tiên 24 tỷ đồng. Khi bán công ty, sau khi trả nợ chỉ còn 20 tỷ để chia.',
          'Nếu hai vòng pari passu, họ chia theo tỷ lệ 6 : 24. Nhà đầu tư seed nhận 20 × 6/30 = 4 tỷ, nhà đầu tư Series A nhận 16 tỷ. Nếu không pari passu mà xếp chồng, Series A nhận đủ 20 tỷ và nhà đầu tư seed không nhận gì.',
        ],
      },
      {
        h: 'Vì sao quan trọng ngay từ vòng đầu',
        p: [
          'Nhà đầu tư vòng sau thường muốn được trả trước, và họ có sức mặc cả vì đang mang tiền tới. Nếu thoả thuận vòng đầu đã ghi rõ các vòng sau sẽ pari passu với vòng hiện tại, nhà đầu tư cũ được bảo vệ, còn founder đỡ phải đứng giữa hai bên khi đàm phán.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Nghĩ pari passu nghĩa là "chia đều". Ngang hàng là cùng lượt, nhưng chia theo tỷ lệ số tiền mỗi bên được hưởng, không phải chia đều đầu người.',
          'Không hỏi pari passu với ai. Câu "cổ phần mới pari passu với cổ phần hiện hữu" chỉ có ý nghĩa khi biết rõ cổ phần hiện hữu đang có những quyền gì.',
        ],
      },
    ],
    faq: [
      {
        q: 'Pari passu nghĩa là gì?',
        a: 'Là ngang hàng: các bên nhận tiền trong cùng một lượt và chia theo tỷ lệ quyền lợi của mỗi bên, không bên nào được ưu tiên trước.',
      },
      {
        q: 'Ngược lại với pari passu là gì?',
        a: 'Là xếp chồng hoặc thứ cấp (senior, subordinated): một bên được trả đủ trước, bên kia chỉ nhận phần còn lại.',
      },
    ],
  },

  'quyen-thong-tin': {
    capNhat: '2026-10-19',
    lienQuan: ['investor-update', 'term-sheet', 'thoa-thuan-co-dong', 'mrr-arr'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Quyền thông tin (information rights) là điều khoản cho nhà đầu tư được nhận báo cáo tài chính và số liệu hoạt động của công ty theo lịch cố định, và thường được quyền kiểm tra sổ sách khi cần.',
          'Đây là điều khoản gần như luôn có trong term sheet, và ít gây tranh cãi. Câu hỏi thật sự là: nhận những gì, bao lâu một lần, và ai được nhận.',
        ],
      },
      {
        h: 'Một gói quyền thông tin thường gồm',
        li: [
          'Báo cáo tài chính quý (hoặc tháng), gửi trong vòng 30 tới 45 ngày sau khi kết thúc kỳ.',
          'Báo cáo tài chính năm, có kiểm toán khi công ty đủ lớn, gửi trong vòng 90 tới 120 ngày sau khi kết thúc năm.',
          'Ngân sách và kế hoạch năm tới, gửi trước khi năm mới bắt đầu.',
          'Quyền đến kiểm tra sổ sách và gặp ban điều hành trong giờ làm việc, có báo trước.',
        ],
      },
      {
        h: 'Luật đã cho gì, hợp đồng phải bổ sung gì',
        p: [
          'Theo Luật Doanh nghiệp 2020, cổ đông hoặc nhóm cổ đông sở hữu từ 5% cổ phần phổ thông mới có quyền xem báo cáo tài chính giữa năm và hằng năm cùng biên bản họp hội đồng quản trị. Một angel nắm 3% thì luật không cho quyền đó, nên phải ghi vào [[thoa-thuan-co-dong|thoả thuận cổ đông]].',
          'Ví dụ: vòng 12 tỷ đồng có một quỹ dẫn dắt 6 tỷ (nắm 10%) và sáu angel mỗi người 1 tỷ (khoảng 1,7%). Cách thường gặp là chỉ cho "nhà đầu tư lớn", ví dụ nắm từ 5%, nhận báo cáo quý chi tiết, còn mọi cổ đông nhận [[investor-update|thư cập nhật]] hằng quý.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Cam kết báo cáo hằng tháng cho mọi cổ đông ngay từ vòng angel, rồi không giữ được lịch. Lỡ hẹn với nhà đầu tư còn tệ hơn không hứa.',
          'Mỗi kỳ tính chỉ số một kiểu. Định nghĩa [[mrr-arr|MRR]] hay "khách hàng hoạt động" phải cố định từ đầu, nếu không nhà đầu tư sẽ nghi ngờ mọi con số.',
          'Chỉ báo tin tốt. Tin xấu nói sớm và nhất quán giữ được niềm tin lâu hơn.',
        ],
      },
    ],
    faq: [
      {
        q: 'Startup có phải gửi báo cáo cho mọi cổ đông không?',
        a: 'Không bắt buộc theo cùng một mức. Luật cho cổ đông lớn (từ 5%) quyền xem báo cáo tài chính; quyền rộng hơn hoặc cho cổ đông nhỏ hơn là do hợp đồng thoả thuận.',
      },
      {
        q: 'Quyền thông tin khác thư cập nhật nhà đầu tư thế nào?',
        a: 'Quyền thông tin là nghĩa vụ ghi trong hợp đồng. Thư cập nhật là thói quen tự nguyện, thường ngắn và kể cả câu chuyện phía sau con số. Startup tốt làm cả hai.',
      },
    ],
  },

  'quyen-yeu-cau-mua-lai': {
    capNhat: '2026-10-19',
    lienQuan: ['term-sheet', 'exit-la-gi', 'co-phan-uu-dai', 'thoa-thuan-co-dong'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Quyền yêu cầu mua lại cổ phần (redemption right) cho nhà đầu tư được buộc công ty mua lại cổ phần của mình sau một thời hạn, thường 5 năm, với giá bằng số vốn đã góp cộng một mức lãi, nếu tới lúc đó công ty vẫn chưa có [[exit-la-gi|exit]].',
          'Trên giấy, đây là "lối thoát" cho nhà đầu tư. Thực tế, startup hiếm khi có đủ tiền mặt để mua lại, nên điều khoản này chủ yếu là đòn bẩy để nhà đầu tư có tiếng nói khi công ty chậm lớn.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Quỹ đầu tư 10 tỷ đồng, kèm quyền yêu cầu mua lại sau 5 năm với giá gốc cộng 8% một năm, lãi đơn. Tới năm thứ năm, quỹ có quyền đòi 10 + 10 × 8% × 5 = 14 tỷ.',
          'Công ty đang có doanh thu 30 tỷ, hoà vốn, tiền mặt 3 tỷ. Không trả được 14 tỷ, công ty buộc phải đàm phán: cho quỹ thêm ghế hội đồng quản trị, tìm người mua lại cổ phần của quỹ, hoặc tìm cách bán công ty sớm hơn mong muốn.',
        ],
      },
      {
        h: 'Lưu ý với pháp luật Việt Nam',
        p: [
          'Luật Doanh nghiệp 2020 giới hạn khá chặt việc công ty cổ phần mua lại cổ phần của chính mình: chỉ trong một số trường hợp, có trần về số lượng, và chỉ được thanh toán khi sau đó công ty vẫn trả đủ các khoản nợ. Vì vậy một điều khoản mua lại viết theo mẫu nước ngoài có thể không thực thi được nguyên văn tại Việt Nam.',
          'Cách làm thường gặp là chuyển nghĩa vụ mua lại sang founder hoặc cổ đông lớn, hoặc đặt điều khoản ở công ty mẹ nước ngoài. Cả hai đều cần luật sư xem xét kỹ, nhất là phương án đặt nghĩa vụ lên cá nhân founder.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Đồng ý vì nghĩ công ty chắc chắn exit trong 5 năm. Dữ liệu của các nhà đầu tư thiên thần cho thấy những thương vụ thành công nhất thường mất hơn 8 năm.',
          'Ký nghĩa vụ mua lại bằng tài sản cá nhân của founder mà không có trần và không có thời hạn.',
        ],
      },
    ],
    faq: [
      {
        q: 'Điều khoản mua lại có phổ biến ở vòng sớm không?',
        a: 'Không. Ở vòng angel và seed, điều khoản này ít gặp và nên từ chối. Nó xuất hiện nhiều hơn ở vòng muộn hoặc với nhà đầu tư có thời hạn quỹ ngắn.',
      },
      {
        q: 'Nếu nhà đầu tư nhất quyết đòi thì đàm phán gì?',
        a: 'Kéo dài thời hạn, bỏ lãi hoặc giảm lãi, chia thành nhiều đợt thanh toán, chỉ áp dụng khi công ty có đủ tiền hợp pháp để mua, và không đặt nghĩa vụ lên tài sản cá nhân của founder.',
      },
    ],
  },

  'dieu-khoan-no-shop': {
    capNhat: '2026-10-19',
    lienQuan: ['term-sheet', 'dong-vong-goi-von', 'due-diligence', 'nha-dau-tu-dan-dat'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Điều khoản no-shop (còn gọi là độc quyền đàm phán) là cam kết trong [[term-sheet|term sheet]] rằng trong một khoảng thời gian, thường 30 tới 60 ngày, startup không chào bán cổ phần, không mời và không đàm phán với nhà đầu tư nào khác.',
          'Phần lớn term sheet không ràng buộc về mặt pháp lý, nhưng no-shop và bảo mật là hai điều khoản thường có hiệu lực ràng buộc. Nhà đầu tư cần nó vì [[due-diligence|thẩm định]] và soạn hợp đồng tốn tiền thuê luật sư, kiểm toán, họ không muốn bị dùng làm đòn bẩy để ép quỹ khác.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Ngày 1/3, startup ký term sheet với quỹ A: đầu tư 15 tỷ đồng ở định giá pre-money 45 tỷ, no-shop 45 ngày. Ngày 20/3, quỹ B gửi email muốn đầu tư ở định giá 60 tỷ.',
          'Trong thời hạn no-shop, startup không được đàm phán với quỹ B, và nhiều term sheet còn buộc phải báo cho quỹ A biết có người tiếp cận. Startup chỉ có thể chờ: nếu ngày 15/4 quỹ A chưa giải ngân và thời hạn hết, khi đó mới được nói chuyện với quỹ B.',
        ],
      },
      {
        h: 'Đàm phán thế nào cho hợp lý',
        li: [
          'Thời hạn ngắn: 30 tới 45 ngày cho vòng angel và seed là đủ.',
          'Tự động chấm dứt nếu nhà đầu tư rút lui, đề nghị hạ định giá, hoặc không gửi bản hợp đồng đầu tiên trong một số ngày nhất định.',
          'Loại trừ những việc không phải gọi vốn: bán hàng, vay vốn lưu động thông thường, phát hành ESOP đã được duyệt.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Ký no-shop dài 90 ngày với nhà đầu tư chưa có cam kết rõ, rồi bị kéo dài thẩm định trong khi runway cạn dần.',
          'Vi phạm no-shop vì nghĩ term sheet "không ràng buộc". Ngoài rủi ro pháp lý, giới đầu tư rất nhỏ và tin đồn đi nhanh.',
        ],
      },
    ],
    faq: [
      {
        q: 'No-shop có bắt buộc không?',
        a: 'Không bắt buộc, nhưng hầu hết nhà đầu tư dẫn dắt đều yêu cầu. Thay vì từ chối, hãy đàm phán thời hạn ngắn và điều kiện chấm dứt rõ ràng.',
      },
      {
        q: 'Trong thời gian no-shop có được nói chuyện với nhà đầu tư đi theo không?',
        a: 'Tuỳ cách viết. Thường được phép mời nhà đầu tư cùng tham gia vòng do quỹ dẫn dắt đặt điều khoản, nhưng không được mời một bên khác thay thế quỹ đó. Hãy ghi rõ điều này trong term sheet.',
      },
    ],
  },

  'cam-doan-bao-dam': {
    capNhat: '2026-10-19',
    lienQuan: ['hop-dong-mua-co-phan-spa', 'due-diligence', 'data-room', 'chuyen-giao-so-huu-tri-tue'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Cam đoan và bảo đảm (representations and warranties) là danh sách những điều công ty, và nhiều khi cả founder, khẳng định là đúng tại thời điểm ký [[hop-dong-mua-co-phan-spa|hợp đồng mua cổ phần]]. Nhà đầu tư dựa vào đó để xuống tiền. Nếu sau này một điều trong danh sách sai, bên cam đoan phải bồi thường.',
          'Đây là phần dài nhất của hợp đồng đầu tư và cũng là phần founder lần đầu hay ký nhanh nhất.',
        ],
      },
      {
        h: 'Những cam đoan thường gặp',
        li: [
          'Công ty được thành lập hợp lệ và có đủ thẩm quyền ký hợp đồng.',
          '[[bang-co-phan|Bảng cổ phần]] chính xác, không có cam kết cổ phần nào chưa ghi nhận.',
          'Sở hữu trí tuệ thuộc về công ty, không nằm trong tên cá nhân founder hay nhân viên cũ.',
          'Báo cáo tài chính trung thực, không có khoản nợ nào chưa công bố.',
          'Không có tranh chấp, khiếu nại hay thanh tra đang diễn ra.',
          'Tuân thủ thuế, bảo hiểm xã hội, lao động và bảo vệ dữ liệu cá nhân.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Quỹ đầu tư 15 tỷ đồng. Hợp đồng có cam đoan "công ty không có nghĩa vụ thuế hay bảo hiểm xã hội nào quá hạn". Sáu tháng sau, đợt kiểm tra phát hiện công ty nợ bảo hiểm xã hội 800 triệu đồng từ hai năm trước, kèm tiền phạt.',
          'Nếu hợp đồng quy định bồi thường, nhà đầu tư có thể đòi khoản thiệt hại tương ứng. Nếu founder cam đoan với tư cách cá nhân, khoản này có thể rơi vào túi founder.',
        ],
      },
      {
        h: 'Cách tự bảo vệ',
        li: [
          'Lập thư công bố ngoại lệ (disclosure letter): liệt kê trước những vấn đề đã biết. Điều gì đã công bố thì nhà đầu tư không đòi bồi thường được nữa. Công bố càng đầy đủ, rủi ro càng thấp.',
          'Đặt trần trách nhiệm, thường không vượt quá số tiền đầu tư, và thời hạn khiếu nại, thường 12 tới 24 tháng.',
          'Thêm "theo hiểu biết tốt nhất của công ty" cho những cam đoan founder không thể kiểm chứng hoàn toàn.',
          'Tách rõ cam đoan của công ty và của cá nhân founder. Cá nhân founder chỉ nên cam đoan những điều nằm trong tầm kiểm soát của mình.',
        ],
      },
    ],
    faq: [
      {
        q: 'Cam đoan và bảo đảm khác thẩm định thế nào?',
        a: 'Thẩm định là nhà đầu tư tự kiểm tra. Cam đoan là bạn khẳng định bằng văn bản. Hai thứ bổ sung cho nhau: điều gì thẩm định không phát hiện được thì cam đoan bảo vệ nhà đầu tư.',
      },
      {
        q: 'Founder có nên ký cam đoan với tư cách cá nhân không?',
        a: 'Nhà đầu tư Việt Nam khá hay yêu cầu điều này. Nếu phải ký, hãy giới hạn ở những điều founder thực sự biết, có trần số tiền và thời hạn rõ ràng.',
      },
    ],
  },

  'vong-tang-gia': {
    capNhat: '2026-10-19',
    lienQuan: ['vong-giam-gia', 'pre-money-post-money', 'pha-loang-toan-phan', 'phan-ky-dinh-gia', 'cot-moc-goi-von'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Vòng tăng giá (up round) là vòng gọi vốn mà giá mỗi cổ phần cao hơn giá ở vòng trước. Ngược lại là [[vong-giam-gia|vòng giảm giá]].',
          'Điểm cần nhớ: so sánh bằng giá mỗi cổ phần, không phải bằng con số định giá. Định giá có thể tăng gấp ba trong khi giá mỗi cổ phần chỉ tăng gấp đôi, vì số cổ phần đã tăng lên ở giữa hai vòng.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Vòng seed: định giá pre-money 30 tỷ đồng, công ty có 1.000.000 cổ phần, giá 30.000 đồng một cổ phần.',
          'Giữa hai vòng, công ty mở quỹ ESOP và SAFE được chuyển đổi, số cổ phần [[pha-loang-toan-phan|pha loãng toàn phần]] tăng lên 1.500.000. Vòng Series A định giá pre-money 90 tỷ. Giá mỗi cổ phần là 90 tỷ ÷ 1.500.000 = 60.000 đồng.',
          'Tiêu đề báo chí sẽ viết "định giá tăng gấp ba", nhưng với nhà đầu tư seed, cổ phần của họ chỉ tăng gấp đôi.',
        ],
      },
      {
        h: 'Vì sao vòng tăng giá quan trọng',
        p: [
          'Vòng tăng giá cho thấy công ty đã đạt [[cot-moc-goi-von|cột mốc]] đủ để thị trường trả giá cao hơn. Nó giữ tinh thần đội ngũ, không kích hoạt [[chong-pha-loang|điều khoản chống pha loãng]], và giúp các vòng sau dễ hơn.',
          'Đó cũng là lý do không nên đẩy định giá vòng đầu quá cao. Định giá vòng sớm cao làm hạn chế dư địa tăng giá và làm tăng khả năng phải gọi vòng giảm giá khi cần thêm vốn.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'So định giá hai vòng thay vì so giá mỗi cổ phần.',
          'Chấp nhận "vòng tăng giá trá hình": giá cổ phần cao hơn nhưng kèm ưu tiên thanh toán gấp 2 lần hoặc chứng quyền lớn, khiến giá trị thật của thương vụ thấp hơn vòng trước.',
        ],
      },
    ],
    faq: [
      {
        q: 'Vòng ngang giá là gì?',
        a: 'Là vòng có giá mỗi cổ phần bằng vòng trước (flat round). Thường là tín hiệu công ty tăng trưởng chậm hơn kế hoạch, nhưng vẫn tốt hơn vòng giảm giá.',
      },
      {
        q: 'Giá cổ phần nên tăng bao nhiêu giữa hai vòng?',
        a: 'Không có chuẩn cố định. Điều quan trọng là bước tăng phải dựa trên cột mốc đạt được, ví dụ doanh thu định kỳ tăng nhiều lần, chứ không chỉ vì thời gian trôi qua.',
      },
    ],
  },

  'co-phan-doi-cong-suc': {
    capNhat: '2026-10-19',
    lienQuan: ['chia-co-phan-dong-sang-lap', 'vesting-la-gi', 'esop-la-gi', 'co-phan-chet', 'von-dieu-le'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Cổ phần đổi bằng công sức (sweat equity) là cổ phần trao cho người đóng góp bằng sức lao động, thời gian và chuyên môn thay vì bằng tiền. Founder làm việc nhiều năm không lương, hay kỹ sư đầu tiên nhận lương thấp hơn thị trường để đổi lấy cổ phần, đều đang góp "công sức".',
          'Đây là cách phổ biến nhất để startup chưa có tiền vẫn có được người giỏi. Nhưng nó cũng là nguồn tranh chấp phổ biến nhất giữa các đồng sáng lập.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Một kỹ sư có lương thị trường 40 triệu đồng một tháng đồng ý làm cho startup với lương 15 triệu, đổi lấy 5% cổ phần vesting trong 4 năm. Phần lương bỏ qua là 25 triệu × 48 tháng = 1,2 tỷ đồng.',
          'Nếu startup đang được định giá 20 tỷ, 5% tương đương 1 tỷ. Thoả thuận này gần như ngang giá, và kỹ sư được hưởng thêm phần tăng giá nếu công ty lớn lên. Tính như vậy giúp hai bên nói chuyện bằng số thay vì cảm tính.',
        ],
      },
      {
        h: 'Làm thế nào cho đúng luật ở Việt Nam',
        p: [
          'Luật Doanh nghiệp liệt kê tài sản góp vốn gồm tiền, vàng, quyền sử dụng đất, quyền sở hữu trí tuệ, công nghệ, bí quyết kỹ thuật và tài sản khác định giá được bằng tiền. Công sức lao động không nằm trong danh sách, nên không ghi được "góp vốn bằng công sức" trên giấy tờ.',
          'Cách thường làm: founder góp một số tiền nhỏ theo mệnh giá để sở hữu cổ phần ngay từ đầu, kèm [[vesting-la-gi|vesting]] ghi trong [[thoa-thuan-dong-sang-lap|thoả thuận đồng sáng lập]]; nhân sự sau này nhận cổ phần qua chương trình [[esop-la-gi|ESOP]]. Việc nhận cổ phần có thể phát sinh nghĩa vụ thuế thu nhập cá nhân, nên hỏi kế toán thuế trước khi làm.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Hứa miệng "em sẽ có 10%" mà không có văn bản và không có vesting. Người đó rời đi sau 6 tháng vẫn đòi đủ 10%, tạo ra [[co-phan-chet|cổ phần chết]].',
          'Trả cổ phần cho đơn vị dịch vụ (agency, công ty phần mềm thuê ngoài) thay vì trả tiền. Bên đó không đi cùng công ty lâu dài nhưng giữ cổ phần mãi mãi.',
          'Định giá công sức quá cao so với giá trị thật của công ty ở thời điểm đó.',
        ],
      },
    ],
    faq: [
      {
        q: 'Có được góp vốn bằng công sức vào công ty ở Việt Nam không?',
        a: 'Công sức không nằm trong danh sách tài sản góp vốn của Luật Doanh nghiệp. Thực tế thường làm bằng cách cho người đó mua cổ phần theo mệnh giá kèm vesting, hoặc trao cổ phần qua ESOP.',
      },
      {
        q: 'Nên trao bao nhiêu cổ phần cho người góp công sức?',
        a: 'Bắt đầu bằng phép tính phần lương bỏ qua so với giá trị cổ phần theo định giá hiện tại, rồi điều chỉnh theo mức rủi ro người đó gánh và vai trò của họ. Luôn kèm vesting.',
      },
    ],
  },

  'dong-dau-tu': {
    capNhat: '2026-10-19',
    lienQuan: ['nha-dau-tu-dan-dat', 'nha-dau-tu-thien-than', 'term-sheet', 'bang-co-phan', 'dong-vong-goi-von'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Đồng đầu tư (syndication, co-investment) là khi nhiều nhà đầu tư cùng góp tiền vào một vòng. Thường có một [[nha-dau-tu-dan-dat|nhà đầu tư dẫn dắt]] thẩm định, đặt giá và điều khoản, còn các bên khác đi theo cùng điều kiện.',
          'Ở giai đoạn sớm, nhóm angel cũng hay góp chung qua một pháp nhân đặc biệt (SPV) để đầu tư với tư cách một cổ đông duy nhất.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Startup gọi vòng seed 12 tỷ đồng. Quỹ dẫn dắt cam kết 6 tỷ và đặt định giá. Hai quỹ khác mỗi bên 2 tỷ, bốn angel mỗi người 500 triệu.',
          'Nếu bốn angel vào trực tiếp, [[bang-co-phan|bảng cổ phần]] có thêm bốn cổ đông nhỏ, mỗi lần họp đại hội đồng cổ đông hay ký văn bản đều cần đủ chữ ký. Nếu họ góp chung qua một SPV, bảng cổ phần chỉ thêm một dòng.',
        ],
      },
      {
        h: 'Lợi ích và cái giá',
        p: [
          'Đồng đầu tư giúp vòng gọi vốn lớn hơn khả năng của một nhà đầu tư, chia sẻ rủi ro và mang tới nhiều mạng lưới hỗ trợ. Với startup, có nhiều quỹ cùng tham gia cũng là tín hiệu tốt cho vòng sau.',
          'Cái giá là thời gian: càng nhiều bên, đàm phán và ký kết càng lâu. Nhà đầu tư nhỏ đi theo thường không thẩm định lại, nên chất lượng của nhà đầu tư dẫn dắt quyết định cả vòng.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Gom 15 angel mỗi người 200 triệu khi chưa có ai dẫn dắt. Mỗi người chờ người khác quyết trước, vòng kéo dài nhiều tháng.',
          'Để mỗi nhà đầu tư nhỏ có điều khoản riêng. Điều khoản phải giống nhau cho mọi bên trong cùng một vòng.',
          'Không kiểm tra quyền và nghĩa vụ của SPV: ai đại diện biểu quyết, ai ký giấy tờ khi bán công ty.',
        ],
      },
    ],
    faq: [
      {
        q: 'Startup có cần nhà đầu tư dẫn dắt không?',
        a: 'Hầu như luôn cần. Nhà đầu tư đi theo thường chỉ cam kết khi đã có một bên đặt giá và điều khoản. Tìm được người dẫn dắt là bước khó nhất của một vòng.',
      },
      {
        q: 'SPV là gì?',
        a: 'Là một công ty hoặc quỹ lập ra chỉ để gom tiền của nhiều nhà đầu tư nhỏ và đầu tư vào một startup. Startup chỉ phải làm việc với một cổ đông thay vì nhiều người.',
      },
    ],
  },

  'magic-number-saas': {
    capNhat: '2026-10-19',
    lienQuan: ['cac-la-gi', 'payback-period', 'burn-multiple', 'mrr-arr', 'kinh-te-don-vi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Magic number đo hiệu quả bán hàng của công ty phần mềm theo thuê bao: mỗi đồng chi cho bán hàng và marketing tạo ra bao nhiêu đồng doanh thu định kỳ năm tăng thêm.',
          'Cách tính phổ biến: lấy doanh thu định kỳ của quý này trừ quý trước, nhân 4 để quy ra năm, rồi chia cho chi phí bán hàng và marketing của quý trước.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Doanh thu định kỳ quý 2 là 1,5 tỷ đồng, quý 3 là 2,1 tỷ. Phần tăng là 0,6 tỷ, quy ra năm là 2,4 tỷ. Chi phí bán hàng và marketing quý 2 là 2 tỷ. Magic number = 2,4 ÷ 2 = 1,2.',
        ],
        bang: {
          cot: ['Magic number', 'Ý nghĩa thường được hiểu'],
          hang: [
            ['Trên 1', 'Bán hàng hiệu quả; có thể tăng chi bán hàng và marketing'],
            ['0,5 tới 1', 'Tạm được; cần cải thiện trước khi tăng chi mạnh'],
            ['Dưới 0,5', 'Báo động; chi bán hàng chưa tạo ra tăng trưởng tương xứng'],
          ],
          chuThich: 'Ngưỡng tham chiếu theo Umbrex, How to Analyze a SaaS Company (2024)',
        },
      },
      {
        h: 'Dùng thế nào cho đúng',
        p: [
          'Magic number trả lời câu hỏi nhà đầu tư rất quan tâm: nếu rót thêm tiền vào bán hàng, công ty có tăng trưởng tương ứng không. Nó đi cùng [[payback-period|thời gian hoàn vốn khách hàng]] và [[burn-multiple|burn multiple]] để cho thấy tiền gọi vốn sẽ được dùng hiệu quả đến đâu.',
          'Với startup còn nhỏ, số liệu từng quý dao động mạnh. Nên tính trung bình nhiều quý liên tiếp và giải thích các quý bất thường.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Dùng tổng doanh thu, kể cả phí triển khai một lần, thay vì chỉ doanh thu định kỳ.',
          'Chỉ tính chi phí quảng cáo mà bỏ lương đội bán hàng, hoa hồng và công cụ.',
          'Áp cho mô hình không phải thuê bao. Với thương mại điện tử hay chợ trực tuyến, nên dùng các chỉ số khác như [[cac-la-gi|CAC]] trên lãi gộp mỗi đơn.',
        ],
      },
    ],
    faq: [
      {
        q: 'Magic number bao nhiêu là tốt?',
        a: 'Trên 1 thường được coi là hiệu quả, 0,5 tới 1 là tạm được, dưới 0,5 là đáng lo. Nên xem xu hướng qua nhiều quý hơn là một con số đơn lẻ.',
      },
      {
        q: 'Magic number khác LTV/CAC thế nào?',
        a: 'LTV/CAC nhìn từng khách hàng trong cả vòng đời. Magic number nhìn tổng thể cả bộ máy bán hàng trong một quý, nên phản ánh nhanh hơn khi hiệu quả thay đổi.',
      },
    ],
  },

  'arpu-acv': {
    capNhat: '2026-10-19',
    lienQuan: ['mrr-arr', 'ltv-la-gi', 'cac-la-gi', 'kinh-te-don-vi', 'payback-period'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'ARPU (average revenue per user) là doanh thu bình quân trên mỗi khách hàng trong một kỳ, thường tính theo tháng. ACV (annual contract value) là giá trị bình quân một năm của mỗi hợp đồng, không tính các khoản thu một lần.',
          'ARPU hay dùng cho sản phẩm có nhiều khách nhỏ, trả tiền theo tháng. ACV hay dùng cho phần mềm bán cho doanh nghiệp theo hợp đồng nhiều năm.',
        ],
      },
      {
        h: 'Ví dụ bằng số cụ thể',
        p: [
          'Phần mềm quản lý phòng khám có 120 khách trả tiền, doanh thu định kỳ tháng 360 triệu đồng. ARPU = 360 triệu ÷ 120 = 3 triệu đồng một khách một tháng.',
          'Một chuỗi bệnh viện ký hợp đồng 3 năm trị giá 540 triệu đồng, cộng 60 triệu phí triển khai. ACV = 540 triệu ÷ 3 = 180 triệu đồng. Phí triển khai không tính vào ACV vì nó không lặp lại.',
        ],
      },
      {
        h: 'Vì sao nhà đầu tư hỏi',
        p: [
          'ARPU và ACV quyết định cách công ty phải bán hàng. ACV vài triệu đồng một năm thì không thể nuôi đội bán hàng gặp trực tiếp từng khách, khách phải tự đăng ký và tự dùng. ACV vài trăm triệu thì đủ để có người bán hàng, nhưng chu kỳ bán dài hơn nhiều.',
          'Chúng cũng là đầu vào của [[ltv-la-gi|LTV]] và [[payback-period|thời gian hoàn vốn]]. ARPU tăng theo thời gian, nhờ khách nâng gói hoặc mua thêm, là một trong những tín hiệu tốt nhất về giá trị sản phẩm.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chia doanh thu cho cả người dùng miễn phí mà không nói rõ. ARPU trên người dùng và ARPU trên khách trả tiền khác nhau rất xa.',
          'Gộp phí triển khai, phần cứng hay dịch vụ một lần vào ACV để con số đẹp hơn.',
          'Báo tổng giá trị hợp đồng (TCV) nhiều năm như thể đó là doanh thu một năm.',
        ],
      },
    ],
    faq: [
      {
        q: 'ARPU và ARPA khác nhau thế nào?',
        a: 'ARPU tính trên mỗi người dùng, ARPA (average revenue per account) tính trên mỗi tài khoản doanh nghiệp, mỗi tài khoản có thể có nhiều người dùng. Phần mềm bán cho doanh nghiệp thường dùng ARPA.',
      },
      {
        q: 'ACV khác ARR thế nào?',
        a: 'ARR là tổng doanh thu định kỳ năm của toàn công ty. ACV là giá trị bình quân một năm của một hợp đồng. ARR xấp xỉ bằng ACV nhân số hợp đồng đang hiệu lực.',
      },
    ],
  },
  /* ─── Bổ sung 22/10/2026: thuật ngữ dùng trong các công cụ định giá, thị trường, term sheet ─── */

  'gia-tri-doanh-nghiep': {
    capNhat: '2026-10-22',
    lienQuan: ['boi-so-doanh-thu', 'boi-so-ebitda', 'nhom-so-sanh', 'pre-money-post-money', 'dinh-gia-startup'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Giá trị doanh nghiệp (enterprise value, viết tắt EV) là giá trị của toàn bộ hoạt động kinh doanh, không phụ thuộc công ty đang giữ bao nhiêu tiền mặt hay đang vay bao nhiêu. Muốn ra giá trị phần vốn của cổ đông, tức con số dùng để đàm phán định giá, phải cộng tiền mặt và trừ nợ vay.',
          'Lý do có hai con số: người mua công ty mua cả hoạt động kinh doanh lẫn khoản nợ đi kèm. Hai công ty kinh doanh giống hệt nhau, một bên còn 5 tỷ tiền mặt, một bên đang nợ 5 tỷ, thì giá trị doanh nghiệp bằng nhau nhưng cổ phần của bên thứ nhất đáng giá hơn 10 tỷ.',
        ],
      },
      {
        h: 'Cách đi từ giá trị doanh nghiệp tới giá trị cổ đông',
        p: [
          'Giá trị phần vốn cổ đông = Giá trị doanh nghiệp + Tiền mặt − Nợ vay.',
        ],
        sau: [
          'Ví dụ: một startup phần mềm có doanh thu 12 tháng gần nhất 6 tỷ đồng. Nhóm doanh nghiệp so sánh có bội số doanh thu khoảng 3 lần, nên giá trị doanh nghiệp khoảng 18 tỷ. Công ty đang có 2 tỷ tiền mặt và khoản vay ngân hàng 1 tỷ. Giá trị phần vốn cổ đông là 18 + 2 − 1 = 19 tỷ. Đây mới là con số gần với định giá pre-money mà nhà đầu tư sẽ bàn.',
        ],
      },
      {
        h: 'Vì sao các bội số định giá đều tính trên giá trị doanh nghiệp',
        p: [
          '[[boi-so-doanh-thu|Bội số doanh thu]] và [[boi-so-ebitda|bội số EBITDA]] lấy từ [[nhom-so-sanh|nhóm so sánh]] đều là giá trị doanh nghiệp chia cho doanh thu hoặc EBITDA. Doanh thu và EBITDA được tạo ra từ hoạt động kinh doanh, trước khi trả lãi vay, nên phải so với giá trị của hoạt động kinh doanh, không so với giá cổ phần. Nhân bội số xong thì nhớ bước cộng tiền mặt, trừ nợ.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Nhân bội số rồi coi luôn kết quả là định giá pre-money, bỏ qua tiền mặt và nợ. Công ty đang vay nhiều sẽ tự định giá cao hơn thực tế.',
          'Tính tiền vừa gọi được vào tiền mặt khi định giá vòng này. Tiền của vòng đang gọi chưa có, nó nằm ở phần post-money.',
          'Lấy bội số tính trên giá cổ phần (vốn hoá thị trường) của công ty niêm yết rồi áp lên giá trị doanh nghiệp, hoặc ngược lại.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Công cụ [[/cong-cu/dinh-gia-co-doanh-thu|Định giá khi đã có doanh thu]] có sẵn ô tiền mặt và nợ vay, tự làm bước cộng trừ này và hiện rõ trong bảng chi tiết.',
        ],
      },
    ],
    faq: [
      { q: 'Giá trị doanh nghiệp là gì?', a: 'Là giá trị toàn bộ hoạt động kinh doanh, chưa tính tiền mặt và nợ vay. Cộng tiền mặt, trừ nợ vay ra giá trị phần vốn cổ đông.' },
      { q: 'Giá trị doanh nghiệp khác định giá pre-money thế nào?', a: 'Định giá pre-money là giá trị phần vốn cổ đông trước khi nhận tiền vòng mới. Nó bằng giá trị doanh nghiệp cộng tiền mặt hiện có, trừ nợ vay.' },
    ],
  },

  'boi-so-ebitda': {
    capNhat: '2026-10-22',
    lienQuan: ['ebitda-la-gi', 'gia-tri-doanh-nghiep', 'boi-so-doanh-thu', 'nhom-so-sanh', 'dinh-gia-dcf'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Bội số EBITDA (EV/EBITDA) là [[gia-tri-doanh-nghiep|giá trị doanh nghiệp]] chia cho [[ebitda-la-gi|EBITDA]] 12 tháng gần nhất. Lấy bội số của các doanh nghiệp tương tự nhân với EBITDA của công ty bạn sẽ ra một khoảng giá trị doanh nghiệp tham chiếu.',
          'Cách này hợp với công ty đã có lãi ổn định ít nhất vài quý liên tiếp. Startup còn lỗ thì EBITDA âm, nhân bội số không ra kết quả có nghĩa, nên thường dùng [[boi-so-doanh-thu|bội số doanh thu]] thay thế.',
        ],
      },
      {
        h: 'Ví dụ bằng số',
        p: [
          'Một chuỗi bán lẻ có EBITDA 12 tháng là 3 tỷ đồng. Các công ty bán lẻ niêm yết trên HOSE có EV/EBITDA từ 6 đến 12 lần. Giá trị doanh nghiệp tham chiếu nằm trong khoảng 18 đến 36 tỷ. Vì công ty nhỏ hơn và cổ phần khó bán lại hơn công ty niêm yết, nhà đầu tư thường trừ thêm một khoản [[chiet-khau-kho-chuyen-nhuong|chiết khấu]], ví dụ 30%, còn khoảng 12,6 đến 25,2 tỷ trước khi cộng tiền mặt và trừ nợ.',
        ],
      },
      {
        h: 'Công ty bạn đứng ở đâu trong khoảng',
        li: [
          'Nửa trên: tăng trưởng nhanh hơn nhóm so sánh, biên EBITDA cao hơn, khách hàng phân tán, không phụ thuộc vài khách lớn.',
          'Nửa dưới: tăng trưởng chậm, lợi nhuận dao động theo mùa, phụ thuộc một hai hợp đồng lớn hoặc vào chính người sáng lập.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Dùng EBITDA kế hoạch năm sau thay cho EBITDA 12 tháng đã thực hiện. Nhà đầu tư sẽ hỏi số đã có trong sổ sách.',
          'Để nguyên khoản thu chi bất thường một lần, ví dụ tiền thanh lý tài sản, làm EBITDA đẹp hơn thực tế.',
          'Lấy bội số của các thương vụ nước ngoài, nơi mặt bằng định giá khác hẳn Việt Nam.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Chọn thẻ "Đã có lãi ổn định" trong công cụ [[/cong-cu/dinh-gia-co-doanh-thu|Định giá khi đã có doanh thu]] để định giá theo bội số EBITDA, kèm đối chiếu [[phuong-phap-vc|phương pháp VC]].',
        ],
      },
    ],
    faq: [
      { q: 'Bội số EBITDA là gì?', a: 'Là giá trị doanh nghiệp chia cho EBITDA 12 tháng. Nhân bội số của doanh nghiệp tương tự với EBITDA của bạn ra giá trị doanh nghiệp tham chiếu.' },
      { q: 'Khi nào dùng bội số EBITDA thay vì bội số doanh thu?', a: 'Khi công ty đã có lãi ổn định. Còn lỗ thì EBITDA âm, bội số EBITDA không dùng được, nên dùng bội số doanh thu.' },
      { q: 'Bội số EBITDA ở Việt Nam thường bao nhiêu?', a: 'Tuỳ ngành và thời điểm. Cách đáng tin nhất là tự tính từ báo cáo tài chính của các công ty cùng ngành đang niêm yết, thay vì lấy một con số chung.' },
    ],
  },

  'nhom-so-sanh': {
    capNhat: '2026-10-22',
    lienQuan: ['boi-so-doanh-thu', 'boi-so-ebitda', 'gia-tri-doanh-nghiep', 'chiet-khau-kho-chuyen-nhuong', 'dinh-gia-startup'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Nhóm so sánh (comparables, hay comps) là hai đến năm doanh nghiệp niêm yết hoặc thương vụ đã công bố, cùng ngành và gần quy mô với công ty bạn, dùng làm thước đo cho định giá. Từ nhóm này bạn tính ra bội số, ví dụ [[gia-tri-doanh-nghiep|giá trị doanh nghiệp]] chia doanh thu, rồi áp lên số liệu của mình.',
          'Ý tưởng giống định giá một căn hộ: xem các căn tương tự trong cùng toà nhà vừa bán bao nhiêu tiền mỗi mét vuông, rồi nhân với diện tích căn của mình.',
        ],
      },
      {
        h: 'Chọn nhóm so sánh thế nào',
        li: [
          'Cùng mô hình kinh doanh: phần mềm thuê bao so với phần mềm thuê bao, không so với công ty gia công phần mềm.',
          'Cùng thị trường: ưu tiên doanh nghiệp niêm yết trên HOSE, HNX hoặc thương vụ ở Việt Nam và Đông Nam Á.',
          'Ghi lại bội số thấp nhất và cao nhất, không chỉ lấy một con số trung bình.',
        ],
        sau: [
          'Ví dụ: bạn tìm được ba công ty công nghệ niêm yết có giá trị doanh nghiệp trên doanh thu lần lượt 1,8; 3,2 và 5,5 lần. Doanh thu 12 tháng của bạn là 6 tỷ đồng, nên khoảng giá trị doanh nghiệp tham chiếu là 10,8 đến 33 tỷ. Startup nhỏ và cổ phần khó bán lại, nên thường trừ thêm [[chiet-khau-kho-chuyen-nhuong|chiết khấu]] khoảng 30%.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chọn toàn công ty đầu ngành có bội số cao nhất để định giá mình cao lên. Nhà đầu tư sẽ tự lập nhóm so sánh của họ.',
          'So với công ty nước ngoài lớn gấp trăm lần. Quy mô và thị trường khác nhau thì bội số không còn ý nghĩa.',
          'Bỏ qua chiết khấu quy mô nhỏ và khó chuyển nhượng khi áp bội số của công ty niêm yết lên startup.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Nhập bội số thấp nhất và cao nhất của nhóm so sánh vào công cụ [[/cong-cu/dinh-gia-co-doanh-thu|Định giá khi đã có doanh thu]]. Công cụ dùng bốn tín hiệu (tăng trưởng, biên lợi nhuận, độ ổn định, giữ chân khách) để đặt bạn ở nửa trên hay nửa dưới khoảng.',
        ],
      },
    ],
    faq: [
      { q: 'Nhóm so sánh là gì?', a: 'Là các doanh nghiệp hoặc thương vụ cùng ngành, gần quy mô, dùng làm thước đo để suy ra bội số định giá cho công ty bạn.' },
      { q: 'Tìm số liệu nhóm so sánh ở đâu?', a: 'Báo cáo tài chính công bố của doanh nghiệp niêm yết trên HOSE, HNX; thông cáo của quỹ và báo chí về các thương vụ đã công bố giá trị.' },
    ],
  },

  'uoc-luong-tu-tren-xuong': {
    capNhat: '2026-10-22',
    lienQuan: ['quy-mo-thi-truong', 'bottom-up-market-sizing', 'pitch-deck-la-gi', 'mo-hinh-tai-chinh'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Ước lượng thị trường từ trên xuống (top-down) là lấy quy mô toàn ngành từ một báo cáo nghiên cứu, rồi nhân với một tỷ lệ phần trăm giả định để ra phần thị trường của mình. Cách này nhanh, nhưng thường bị nhà đầu tư đặt nghi vấn.',
          'Lý do: tỷ lệ phần trăm được chọn tuỳ ý và không nói gì về việc công ty sẽ giành từng khách hàng bằng cách nào.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Một startup giáo dục viết trong pitch deck: "Thị trường giáo dục trực tuyến Việt Nam đạt 3 tỷ USD, chúng tôi chỉ cần chiếm 1% là có 30 triệu USD doanh thu." Câu hỏi đầu tiên của nhà đầu tư sẽ là: vì sao 1%, và 1% đó là bao nhiêu học viên, trả bao nhiêu tiền, đến từ kênh nào?',
          'Cùng bài toán theo cách [[bottom-up-market-sizing|từ dưới lên]]: có 120.000 học sinh lớp 12 ở các thành phố bạn bán được, mỗi em trả 2,4 triệu đồng một năm, mục tiêu 5% trong 5 năm. Con số ra nhỏ hơn nhiều, nhưng từng giả định đều kiểm chứng được.',
        ],
      },
      {
        h: 'Khi nào vẫn dùng được',
        li: [
          'Để kiểm tra chéo: kết quả từ dưới lên không nên vượt quá con số toàn ngành trong báo cáo.',
          'Để mô tả bối cảnh ngành ở một slide, miễn là không dùng làm căn cứ cho mục tiêu doanh thu.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Dùng câu "chỉ cần chiếm 1% thị trường". Nhà đầu tư nghe câu này rất nhiều và coi đó là dấu hiệu chưa hiểu khách hàng.',
          'Trích báo cáo cho cả khu vực Đông Nam Á hoặc toàn cầu rồi coi như thị trường của mình.',
          'Không ghi nguồn và năm của báo cáo.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Công cụ [[/cong-cu/quy-mo-thi-truong|Quy mô thị trường]] dựng TAM, SAM, SOM theo cách từ dưới lên và theo dõi bao nhiêu giả định đã ghi nguồn. Đọc thêm [[quy-mo-thi-truong|TAM, SAM, SOM]].',
        ],
      },
    ],
    faq: [
      { q: 'Ước lượng từ trên xuống là gì?', a: 'Là lấy quy mô ngành từ báo cáo rồi nhân với một tỷ lệ giả định để ra phần thị trường của mình.' },
      { q: 'Nên dùng từ trên xuống hay từ dưới lên?', a: 'Dùng từ dưới lên làm con số chính vì từng giả định kiểm chứng được. Từ trên xuống chỉ nên dùng để kiểm tra chéo.' },
    ],
  },

  'uu-tien-tham-gia': {
    capNhat: '2026-10-22',
    lienQuan: ['liquidation-preference', 'co-phan-uu-dai', 'thac-phan-phoi-thanh-ly', 'term-sheet'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Ưu tiên có tham gia chia tiếp (participating preferred) là khi bán công ty, nhà đầu tư lấy lại phần [[liquidation-preference|ưu tiên thanh toán]] trước, rồi vẫn được chia tiếp phần còn lại theo tỷ lệ sở hữu như cổ đông phổ thông. Giới đầu tư hay gọi là "double dip", ăn hai lần.',
          'Thông lệ ở vòng đầu là không tham gia chia tiếp: nhà đầu tư chọn một trong hai, hoặc lấy lại vốn, hoặc chia theo tỷ lệ, bên nào nhiều hơn.',
        ],
      },
      {
        h: 'Ví dụ bằng số',
        p: [
          'Quỹ đầu tư 5 tỷ đồng ở định giá pre-money 20 tỷ, nắm 20%. Ba năm sau công ty được bán với giá 40 tỷ.',
        ],
        li: [
          'Không tham gia chia tiếp: quỹ chọn giữa lấy lại 5 tỷ hoặc 20% của 40 tỷ là 8 tỷ. Quỹ chọn 8 tỷ, cổ đông còn lại nhận 32 tỷ.',
          'Có tham gia chia tiếp: quỹ lấy 5 tỷ trước, rồi thêm 20% của 35 tỷ còn lại là 7 tỷ, tổng 12 tỷ. Cổ đông còn lại chỉ nhận 28 tỷ.',
        ],
        sau: [
          'Chênh lệch 4 tỷ rơi hết vào nhà sáng lập và nhân viên giữ cổ phần phổ thông. Bán giá càng vừa phải, phần chênh lệch càng lớn so với tổng tiền nhận được.',
        ],
      },
      {
        h: 'Phiên bản có trần',
        p: [
          'Một số term sheet thoả hiệp bằng cách đặt trần: nhà đầu tư được chia tiếp cho tới khi nhận đủ, ví dụ, 3 lần vốn. Bán giá đủ cao thì họ chuyển sang chia theo tỷ lệ nếu lợi hơn.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chỉ nhìn bội số ưu tiên (1 lần) mà bỏ qua chữ "participating" trong term sheet.',
          'Chỉ thử kịch bản bán giá rất cao, nơi điều khoản gần như không ảnh hưởng.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Công cụ [[/cong-cu/doc-term-sheet|Đọc term sheet bằng con số]] vẽ số tiền bạn nhận ở mọi mức bán công ty theo từng loại ưu tiên.',
        ],
      },
    ],
    faq: [
      { q: 'Participating preferred là gì?', a: 'Là quyền nhà đầu tư lấy lại phần ưu tiên khi bán công ty rồi vẫn được chia tiếp phần còn lại theo tỷ lệ sở hữu.' },
      { q: 'Có nên chấp nhận ưu tiên có tham gia chia tiếp?', a: 'Ở vòng đầu, thông lệ là không tham gia chia tiếp. Nếu phải chấp nhận, nên thương lượng một mức trần và thử các kịch bản bán ở mức vừa phải trước khi ký.' },
    ],
  },

  'boi-so-nhan-von': {
    capNhat: '2026-10-22',
    lienQuan: ['phuong-phap-vc', 'exit-la-gi', 'quy-dau-tu-mao-hiem', 'pha-loang-co-phan'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Bội số nhân vốn (MOIC, multiple on invested capital) là tổng tiền nhà đầu tư nhận về chia cho tiền bỏ vào. Bỏ 5 tỷ, khi [[exit-la-gi|exit]] nhận về 50 tỷ là nhân vốn 10 lần.',
          '[[quy-dau-tu-mao-hiem|Quỹ đầu tư mạo hiểm]] giai đoạn sớm cần mỗi khoản đầu tư có khả năng nhân 10 lần trở lên, vì phần lớn các khoản khác sẽ không hoàn vốn. Một hai khoản thành công phải gánh cả danh mục.',
        ],
      },
      {
        h: 'Bội số nhân vốn quyết định định giá thế nào',
        p: [
          'Theo [[phuong-phap-vc|phương pháp VC]], nhà đầu tư đi ngược từ giá trị công ty lúc exit. Ví dụ: công ty kỳ vọng bán được 300 tỷ sau 5 năm. Qua các vòng sau, phần của nhà đầu tư vòng này bị [[pha-loang-co-phan|pha loãng]] còn 70%. Họ cần nhân vốn 10 lần, nên định giá post-money tối đa họ chấp nhận là 300 × 70% ÷ 10 = 21 tỷ. Nếu họ đòi 20 lần, con số giảm còn 10,5 tỷ.',
          'Nhân 10 lần trong 5 năm tương đương lợi nhuận khoảng 58% mỗi năm. Con số nghe rất cao, nhưng phản ánh đúng rủi ro của đầu tư giai đoạn sớm.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Nghĩ nhà đầu tư "tham" khi đòi nhân 10 lần. Đó là cách họ bù cho những khoản mất trắng.',
          'Tính giá trị exit mà quên phần pha loãng qua các vòng sau.',
          'Dựng kế hoạch exit với mức tăng trưởng không tưởng chỉ để đẩy định giá lên.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Phần "Phương pháp VC" trong công cụ [[/cong-cu/dinh-gia-co-doanh-thu|Định giá khi đã có doanh thu]] cho bạn chỉnh số lần nhân vốn và thấy ngay định giá thay đổi ra sao.',
        ],
      },
    ],
    faq: [
      { q: 'Bội số nhân vốn là gì?', a: 'Là tổng tiền nhà đầu tư nhận về chia cho tiền bỏ vào. Bỏ 5 tỷ nhận về 50 tỷ là nhân vốn 10 lần.' },
      { q: 'Vì sao quỹ cần nhân vốn 10 lần?', a: 'Vì phần lớn khoản đầu tư giai đoạn sớm không hoàn vốn. Vài khoản thành công phải nhân nhiều lần để cả quỹ có lãi.' },
    ],
  },

  'gia-moi-co-phan': {
    capNhat: '2026-10-22',
    lienQuan: ['pre-money-post-money', 'bang-co-phan', 'pha-loang-toan-phan', 'option-pool-shuffle', 'safe-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Giá mỗi cổ phần là định giá pre-money chia cho tổng số cổ phần trước vòng. Nhà đầu tư mới mua cổ phần ở đúng giá này, nên số cổ phần họ nhận bằng số tiền đầu tư chia cho giá mỗi cổ phần.',
          'Điểm cần chú ý là "tổng số cổ phần trước vòng" thường được tính theo [[pha-loang-toan-phan|pha loãng toàn phần]]: gồm cả cổ phần dành cho ESOP, kể cả phần ESOP mở rộng theo yêu cầu của nhà đầu tư, và cổ phần từ SAFE vừa chuyển đổi.',
        ],
      },
      {
        h: 'Ví dụ bằng số',
        p: [
          'Công ty có 1.000.000 cổ phần, định giá pre-money 20 tỷ đồng. Nếu không có gì khác, giá mỗi cổ phần là 20.000 đồng. Nhà đầu tư rót 5 tỷ nhận 250.000 cổ phần, tương đương 20% sau vòng.',
          'Nếu term sheet yêu cầu mở rộng ESOP lên 10% trong phần pre-money, công ty phải phát hành thêm cổ phần ESOP trước khi tính giá. Tổng cổ phần trước vòng tăng lên, giá mỗi cổ phần giảm, và cổ đông hiện hữu bị pha loãng thêm dù định giá pre-money ghi trên giấy vẫn là 20 tỷ. Đây chính là [[option-pool-shuffle|option pool shuffle]].',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chỉ đọc định giá pre-money mà không hỏi tổng số cổ phần dùng để chia gồm những gì.',
          'Quên các SAFE đang chờ chuyển đổi. SAFE chuyển thành cổ phần ngay trong vòng này và làm giá mỗi cổ phần thấp đi.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Công cụ [[/cong-cu/safe-va-trai-phieu-chuyen-doi|SAFE và trái phiếu chuyển đổi]] tính giá mỗi cổ phần sau khi SAFE chuyển đổi và ESOP mở rộng, kèm [[bang-co-phan|bảng cổ phần]] trước và sau vòng.',
        ],
      },
    ],
    faq: [
      { q: 'Giá mỗi cổ phần tính thế nào?', a: 'Lấy định giá pre-money chia cho tổng số cổ phần trước vòng, thường tính theo pha loãng toàn phần gồm cả ESOP và SAFE chuyển đổi.' },
      { q: 'Vì sao giá mỗi cổ phần quan trọng hơn định giá ghi trên giấy?', a: 'Vì cùng một định giá pre-money, tổng số cổ phần dùng để chia khác nhau sẽ cho giá khác nhau, và phần sở hữu của bạn sau vòng khác nhau.' },
    ],
  },
  'doanh-thu-chay-nam': {
    capNhat: '2026-10-22',
    lienQuan: ['mrr-arr', 'tang-truong-mom', 'boi-so-doanh-thu', 'traction-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Doanh thu chạy năm (run-rate) là doanh thu tháng gần nhất nhân 12. Nó trả lời câu hỏi: nếu từ giờ mỗi tháng đều bán được như tháng vừa rồi, một năm công ty thu bao nhiêu.',
          'Con số này khác với doanh thu 12 tháng đã thực thu (LTM). Startup đang tăng nhanh thì doanh thu chạy năm luôn cao hơn hẳn doanh thu 12 tháng, vì các tháng đầu năm còn nhỏ.',
        ],
      },
      {
        h: 'Ví dụ bằng số',
        p: [
          'Một startup có doanh thu tháng 1 là 120 triệu đồng, tăng dần tới tháng 12 là 226 triệu. Tổng 12 tháng thực thu khoảng 2 tỷ. Doanh thu chạy năm là 226 triệu × 12 = 2,7 tỷ.',
          'Cả hai con số đều đúng, nhưng nói "doanh thu năm 2,7 tỷ" mà không nói rõ là chạy năm thì nhà đầu tư sẽ coi là phóng đại khi đối chiếu với báo cáo tài chính.',
        ],
      },
      {
        h: 'Khi nào dùng con số nào',
        li: [
          'Doanh thu chạy năm: hợp với mô hình thuê bao, doanh thu lặp lại đều hằng tháng, nơi tháng gần nhất phản ánh đúng quy mô hiện tại. Với SaaS, nó gần với [[mrr-arr|ARR]].',
          'Doanh thu 12 tháng thực thu: hợp với doanh thu theo dự án, theo mùa, hoặc khi tháng gần nhất có một hợp đồng lớn bất thường.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Lấy tháng cao nhất trong năm, ví dụ tháng Tết của ngành bán lẻ, rồi nhân 12.',
          'Gọi doanh thu chạy năm là "doanh thu năm" trong pitch deck mà không ghi chú cách tính.',
          'Áp [[boi-so-doanh-thu|bội số doanh thu]] của nhóm so sánh (thường tính trên 12 tháng thực thu) lên doanh thu chạy năm, làm định giá cao lên.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Công cụ [[/cong-cu/chi-so-tang-truong|Chỉ số tăng trưởng]] hiện cả hai con số cạnh nhau và cảnh báo khi doanh thu chạy năm cao gấp đôi doanh thu thực thu.',
        ],
      },
    ],
    faq: [
      { q: 'Doanh thu chạy năm là gì?', a: 'Là doanh thu tháng gần nhất nhân 12, cho biết quy mô một năm nếu giữ nguyên nhịp bán hiện tại.' },
      { q: 'Doanh thu chạy năm khác ARR thế nào?', a: 'ARR chỉ tính phần doanh thu định kỳ (thuê bao, hợp đồng lặp lại). Doanh thu chạy năm nhân 12 toàn bộ doanh thu tháng gần nhất, kể cả phần bán một lần.' },
    ],
  },

  'quy-mo-khoan-dau-tu': {
    capNhat: '2026-10-22',
    lienQuan: ['khau-vi-dau-tu', 'so-tien-goi-von', 'nha-dau-tu-dan-dat', 'dong-dau-tu', 'quy-dau-tu-mao-hiem'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Quy mô khoản đầu tư (ticket size) là khoảng số tiền một quỹ hoặc nhà đầu tư thường bỏ vào mỗi startup, ví dụ "từ 2 đến 10 tỷ đồng mỗi khoản". Đây là một phần của [[khau-vi-dau-tu|khẩu vị đầu tư]], cùng với ngành, giai đoạn và địa bàn.',
          'Quy mô khoản đầu tư phụ thuộc vào cỡ quỹ. Một quỹ 500 tỷ đầu tư 20 công ty sẽ khó bỏ 500 triệu vào một startup, vì công sức thẩm định và theo dõi một khoản nhỏ cũng tốn ngang khoản lớn.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Bạn cần gọi 3 tỷ đồng ở vòng [[vong-pre-seed|pre-seed]]. Quỹ A công bố đầu tư 5 đến 20 tỷ mỗi khoản, quỹ B đầu tư 1 đến 5 tỷ, một nhóm nhà đầu tư thiên thần thường góp 300 triệu đến 1 tỷ mỗi người.',
          'Quỹ A gần như chắc chắn không xem hồ sơ. Quỹ B hợp nhất để làm [[nha-dau-tu-dan-dat|nhà đầu tư dẫn dắt]]. Nhóm thiên thần có thể [[dong-dau-tu|đồng đầu tư]] phần còn lại.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Gửi hồ sơ cho quỹ lớn nổi tiếng dù số tiền cần gọi nhỏ hơn nhiều so với khoản tối thiểu của họ.',
          'Nghĩ rằng gọi nhiều hơn mức mình cần sẽ hấp dẫn quỹ lớn. Số tiền phải đi ra từ kế hoạch sử dụng vốn, không đi ra từ quỹ mình muốn gặp.',
          'Không hỏi trước quỹ có muốn dẫn dắt vòng hay chỉ góp một phần.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Công cụ [[/cong-cu/quy-phu-hop|Quỹ nào phù hợp với bạn]] đối chiếu số tiền bạn định gọi với quy mô khoản đầu tư quỹ đã công bố, cùng ngành, giai đoạn và loại vốn. Chưa biết nên gọi bao nhiêu thì dùng [[/cong-cu/goi-bao-nhieu|Gọi bao nhiêu và dùng vào việc gì]] trước.',
        ],
      },
    ],
    faq: [
      { q: 'Ticket size là gì?', a: 'Là khoảng số tiền một quỹ thường đầu tư vào mỗi startup. Gọi ngoài khoảng này thì hồ sơ thường bị loại sớm.' },
      { q: 'Tìm quy mô khoản đầu tư của quỹ ở đâu?', a: 'Trang chính thức của quỹ, thông cáo các thương vụ đã công bố, hoặc hỏi trực tiếp qua người giới thiệu. Danh mục nguồn vốn trên site ghi rõ nguồn và ngày kiểm chứng.' },
    ],
  },

  /* ─── Bổ sung 11/2026: toán học của quỹ và 5T ─────────────────── */

  'lp-va-gp': {
    capNhat: '2026-11-01',
    lienQuan: ['quy-dau-tu-mao-hiem', 'phi-quan-ly-quy', 'carry', 'cong-ty-tra-lai-ca-quy'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Một [[quy-dau-tu-mao-hiem|quỹ đầu tư mạo hiểm]] có hai bên. LP (limited partner, người góp vốn) là quỹ hưu trí, gia đình giàu có, tổ chức tài chính hay doanh nghiệp lớn đưa tiền vào quỹ. GP (general partner, người quản lý quỹ) là đội ngũ chọn startup, đàm phán, hỗ trợ sau đầu tư và chịu trách nhiệm trả lợi nhuận cho LP.',
          'Người bạn gặp khi gọi vốn là GP. Tiền họ rót vào startup của bạn không phải tiền của họ, mà là tiền của LP. Vì vậy mọi quyết định của GP đều xoay quanh câu hỏi: khoản này giúp quỹ trả đủ lợi nhuận đã hứa với LP như thế nào.',
        ],
      },
      {
        h: 'GP kiếm tiền thế nào',
        li: [
          '[[phi-quan-ly-quy|Phí quản lý]]: thường khoảng 2% số vốn mỗi năm, dùng trả lương và chi phí vận hành.',
          '[[carry|Carry]]: thường khoảng 20% lợi nhuận, chỉ được chia khi quỹ trả cho LP vượt mức lãi đã thoả thuận.',
          'Phần vốn GP tự góp vào quỹ, thường nhỏ, để cùng chịu rủi ro với LP.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Một quỹ 500 tỷ đồng, kỳ hạn 10 năm. LP góp gần như toàn bộ 500 tỷ. GP nhận phí quản lý khoảng 10 tỷ mỗi năm để vận hành, và chỉ có thêm thu nhập lớn khi quỹ trả về cho LP nhiều hơn mức lãi đã cam kết.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Nghĩ rằng quỹ có thể đầu tư vào bất cứ công ty tốt nào. GP bị ràng buộc bởi điều lệ quỹ đã ký với LP: ngành, giai đoạn, địa bàn, quy mô khoản đầu tư.',
          'Không hỏi quỹ đang ở năm thứ mấy. Quỹ đã gần hết giai đoạn đầu tư thường chỉ còn tiền cho các công ty cũ trong danh mục.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Công cụ [[/cong-cu/quy-phu-hop|Quỹ nào phù hợp với bạn]] lọc quỹ theo ngành, giai đoạn và số tiền bạn định gọi. Bài [[/playbook/toan-hoc-quy-vc-vi-sao-quy-tu-choi-startup-co-lai|Toán học của quỹ VC]] giải thích vì sao áp lực trả lợi nhuận cho LP quyết định quỹ chọn startup nào.',
        ],
      },
    ],
    faq: [
      { q: 'LP và GP là gì?', a: 'LP là người góp tiền vào quỹ đầu tư, GP là đội ngũ quản lý quỹ. Founder gọi vốn làm việc với GP, nhưng tiền là của LP.' },
      { q: 'Founder có cần biết LP của quỹ là ai không?', a: 'Nên biết khái quát. LP là quỹ nhà nước, tập đoàn hay nhà đầu tư nước ngoài ảnh hưởng tới ngành quỹ được đầu tư, thời hạn quỹ và yêu cầu pháp lý.' },
    ],
  },

  'phi-quan-ly-quy': {
    capNhat: '2026-11-01',
    lienQuan: ['lp-va-gp', 'carry', 'quy-dau-tu-mao-hiem', 'cong-ty-tra-lai-ca-quy'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Phí quản lý (management fee) là khoản quỹ trích mỗi năm để vận hành, thường khoảng 2% số vốn cam kết. Cùng với [[carry]] khoảng 20%, giới đầu tư hay gọi là quy ước 2 và 20.',
          'Phí quản lý trả lương đội ngũ, văn phòng, thẩm định, pháp lý, đi lại. Phần tiền này không được đem đi đầu tư, nên số vốn thực sự rót vào startup luôn ít hơn quy mô quỹ. Nhiều quỹ giảm mức phí sau khi hết giai đoạn tìm khoản đầu tư mới.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Quỹ 500 tỷ đồng, kỳ hạn 10 năm, phí 2% mỗi năm: khoảng 10 tỷ một năm, tổng tối đa khoảng 100 tỷ. Chỉ còn khoảng 400 tỷ để đầu tư.',
          'Nếu LP kỳ vọng nhận về khoảng 3 lần số vốn góp, tức khoảng 1.500 tỷ, thì 400 tỷ đem đi đầu tư phải sinh ra gần 4 lần. Đây là lý do quỹ cần những công ty có thể tăng giá trị rất nhiều lần.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Lấy quy mô quỹ công bố để ước số tiền quỹ còn có thể đầu tư. Một phần đã dành cho phí, một phần dành cho các vòng sau của công ty cũ.',
          'Nghĩ rằng phí quản lý là lợi nhuận của quỹ. Phí chỉ để vận hành; đội ngũ quỹ chỉ có thu nhập lớn từ carry khi quỹ làm ăn tốt.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Bài [[/playbook/toan-hoc-quy-vc-vi-sao-quy-tu-choi-startup-co-lai|Toán học của quỹ VC]] đi từng bước từ phí quản lý tới con số quỹ phải trả về. Công cụ [[/cong-cu/quy-phu-hop|Quỹ nào phù hợp với bạn]] giúp tìm quỹ có quy mô hợp với số tiền bạn cần.',
        ],
      },
    ],
    faq: [
      { q: 'Phí quản lý quỹ là bao nhiêu?', a: 'Phổ biến khoảng 2% số vốn cam kết mỗi năm, nhiều quỹ giảm sau giai đoạn đầu tư. Mức cụ thể ghi trong điều lệ quỹ.' },
      { q: 'Quy ước 2 và 20 là gì?', a: 'Là cách gọi tắt mức thu phổ biến của quỹ: phí quản lý khoảng 2% mỗi năm và carry khoảng 20% lợi nhuận.' },
    ],
  },

  'carry': {
    capNhat: '2026-11-01',
    lienQuan: ['lp-va-gp', 'phi-quan-ly-quy', 'boi-so-nhan-von', 'luat-luy-thua'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Carry (carried interest) là phần lợi nhuận đội ngũ quản lý quỹ được chia, thường khoảng 20%. Điểm quan trọng: GP chỉ nhận carry sau khi đã trả lại vốn cho LP và vượt mức lãi tối thiểu thoả thuận trong điều lệ quỹ (hurdle), mức này nhiều quỹ đặt khoảng 8% mỗi năm.',
          'Carry là lý do GP theo đuổi những khoản có thể thắng rất lớn. Một danh mục toàn công ty "ổn" có thể trả đủ vốn cho LP nhưng không bao giờ sinh ra carry cho GP.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Quỹ 500 tỷ đồng, sau 10 năm trả về 1.500 tỷ. Lợi nhuận là 1.000 tỷ. Nếu đã vượt mức lãi tối thiểu, GP nhận khoảng 20% phần lợi nhuận, tức khoảng 200 tỷ; LP nhận phần còn lại.',
          'Nếu quỹ chỉ trả về 600 tỷ, lợi nhuận 100 tỷ, chưa vượt mức lãi tối thiểu sau 10 năm. GP gần như không có carry.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Nghĩ quỹ hài lòng với khoản đầu tư nhân 2 hoặc 3 lần. Với GP, khoản đó gần như không đóng góp vào carry; thứ họ cần là một hai khoản [[boi-so-nhan-von|nhân vốn]] vài chục lần.',
          'Nhầm carry với phí quản lý. Phí là tiền vận hành có ngay mỗi năm, carry chỉ có khi quỹ thắng.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Bài [[/playbook/toan-hoc-quy-vc-vi-sao-quy-tu-choi-startup-co-lai|Toán học của quỹ VC]] cho thấy năm kịch bản danh mục và kịch bản nào đủ để quỹ có lãi. Hồ sơ của bạn cần cho thấy khả năng thành khoản thắng lớn đó: chấm thử bằng [[/cong-cu/diem-san-sang-goi-von|Điểm sẵn sàng gọi vốn]].',
        ],
      },
    ],
    faq: [
      { q: 'Carry trong quỹ đầu tư là gì?', a: 'Là phần lợi nhuận đội ngũ quản lý quỹ được chia, thường khoảng 20%, chỉ khi quỹ trả cho người góp vốn vượt mức lãi đã thoả thuận.' },
      { q: 'Carry ảnh hưởng gì tới founder?', a: 'Vì GP chỉ có carry khi quỹ thắng lớn, họ ưu tiên startup có khả năng tăng giá trị rất nhiều lần, kể cả khi rủi ro cao hơn.' },
    ],
  },

  'luat-luy-thua': {
    capNhat: '2026-11-01',
    lienQuan: ['cong-ty-tra-lai-ca-quy', 'boi-so-nhan-von', 'carry', 'quy-dau-tu-mao-hiem'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Luật lũy thừa (power law) mô tả cách lợi nhuận của đầu tư mạo hiểm phân bố: không đều quanh mức trung bình, mà dồn vào số rất ít công ty. Phần lớn khoản đầu tư lỗ hoặc chỉ hoà vốn, một hai công ty thắng lớn gánh lợi nhuận cho cả quỹ.',
          'Lý do nằm ở hai đầu: một khoản đầu tư lỗ tối đa 1 lần số vốn, nhưng có thể lãi 50 đến 100 lần. Dữ liệu Correlation Ventures trên hơn 21.000 vòng gọi vốn tại Mỹ cho thấy gần 2/3 số vòng không thu hồi đủ vốn, chỉ khoảng 4% lãi trên 10 lần.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Một quỹ rót 20 tỷ đồng vào mỗi công ty trong 10 công ty. Năm công ty đóng cửa, ba công ty trả lại khoảng 20 tỷ mỗi công ty, một công ty mang về 100 tỷ. Tổng thu khoảng 160 tỷ, chưa bằng 200 tỷ đã rót.',
          'Chỉ cần công ty thứ mười tăng giá trị đủ lớn để mang về 500 tỷ, cả quỹ lãi gần 3 lần. Kết quả của quỹ phụ thuộc gần như hoàn toàn vào một khoản.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Thuyết phục quỹ bằng việc công ty "an toàn, ít rủi ro". Với quỹ, công ty không thể lớn rất nhiều lần cũng là một khoản không giúp được danh mục.',
          'Nghĩ quy tắc 80/20 đã đủ mô tả. Dữ liệu Horsley Bridge cho thấy khoảng 6% khoản đầu tư tạo ra khoảng 60% lợi nhuận, lệch hơn nhiều.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Bài [[/playbook/toan-hoc-quy-vc-vi-sao-quy-tu-choi-startup-co-lai|Toán học của quỹ VC]] có năm kịch bản danh mục bằng số và nguồn dữ liệu. Muốn biết công ty bạn có đường tới quy mô quỹ cần, bắt đầu từ [[/cong-cu/quy-mo-thi-truong|Quy mô thị trường]].',
        ],
      },
    ],
    faq: [
      { q: 'Luật lũy thừa trong đầu tư mạo hiểm là gì?', a: 'Là việc lợi nhuận của quỹ dồn vào rất ít công ty thắng lớn, trong khi phần lớn khoản đầu tư lỗ hoặc hoà vốn.' },
      { q: 'Vì sao quỹ chấp nhận nhiều khoản lỗ?', a: 'Vì mỗi khoản chỉ lỗ tối đa 1 lần vốn, còn một khoản thắng có thể lãi vài chục lần và bù cho tất cả.' },
    ],
  },

  'cong-ty-tra-lai-ca-quy': {
    capNhat: '2026-11-01',
    lienQuan: ['luat-luy-thua', 'quy-mo-thi-truong', 'exit-la-gi', 'phan-ky-dinh-gia'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Công ty trả lại cả quỹ (fund returner) là một khoản đầu tư mà riêng nó mang về số tiền bằng hoặc hơn toàn bộ quy mô quỹ. Vì [[luat-luy-thua|luật lũy thừa]], quỹ chỉ rót vốn khi thấy startup có khả năng trở thành công ty như vậy, dù biết phần lớn sẽ không thành.',
          'Mốc này tính bằng một phép chia: quy mô quỹ chia cho phần trăm cổ phần quỹ còn giữ lúc [[exit-la-gi|thoát vốn]]. Kết quả là giá bán công ty tối thiểu để một mình nó trả lại cả quỹ.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Quỹ 500 tỷ đồng, dự kiến còn giữ 10% cổ phần khi công ty được bán, sau khi bị pha loãng qua các vòng. Công ty phải được bán ở khoảng 5.000 tỷ đồng thì quỹ mới nhận về 500 tỷ.',
          'Nếu kế hoạch 5 đến 7 năm của bạn chỉ dẫn tới giá trị khoảng 500 tỷ, quỹ này khó nói có dù công ty tốt. Một quỹ nhỏ hơn, nhà đầu tư thiên thần hoặc vốn vay có thể hợp hơn.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Tính bằng tỷ lệ cổ phần quỹ mua hôm nay mà quên [[phan-ky-dinh-gia|pha loãng qua các vòng sau]]. Tỷ lệ còn lại lúc thoát vốn thấp hơn nhiều.',
          'Gửi hồ sơ cho quỹ quá lớn so với tiềm năng của công ty. Quỹ càng lớn, mốc trả lại cả quỹ càng cao.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Ước thị trường từ dưới lên bằng [[/cong-cu/quy-mo-thi-truong|Quy mô thị trường]], rồi đặt cạnh mốc trên. Bài [[/playbook/toan-hoc-quy-vc-vi-sao-quy-tu-choi-startup-co-lai|Toán học của quỹ VC]] có bảng mốc cho quỹ 20, 50 và 100 triệu USD.',
        ],
      },
    ],
    faq: [
      { q: 'Fund returner là gì?', a: 'Là một khoản đầu tư riêng nó mang về số tiền bằng hoặc hơn toàn bộ quy mô quỹ. Quỹ tìm startup có khả năng thành khoản như vậy.' },
      { q: 'Tính mốc trả lại cả quỹ thế nào?', a: 'Lấy quy mô quỹ chia cho phần trăm cổ phần quỹ còn giữ khi thoát vốn. Quỹ 500 tỷ giữ 10% thì công ty cần bán được khoảng 5.000 tỷ.' },
    ],
  },

  'don-bay-mo-rong': {
    capNhat: '2026-11-01',
    lienQuan: ['hao-phong-thu', 'kinh-te-don-vi', 'bien-loi-nhuan-gop', 'cong-ty-tra-lai-ca-quy'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Đòn bẩy mở rộng (scalability) là khả năng tăng số khách và doanh thu nhanh hơn nhiều so với tăng chi phí, nhờ công nghệ, tự động hoá hoặc quy trình. Đây là chữ Technology trong 5T nhà đầu tư dùng để chấm startup: Team, TAM, Technology, Traction, Trenches.',
          'Câu hỏi cụ thể nhà đầu tư đặt ra: khi số khách gấp 10 lần, chi phí phục vụ mỗi khách thay đổi thế nào. Nếu gần như không đổi, công ty lớn lên mà không giàu lên.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Một dịch vụ kế toán thuê ngoài: mỗi kế toán lương 15 triệu đồng phục vụ được khoảng 30 khách. Muốn có 3.000 khách cần 100 kế toán, chi phí tăng gần theo số khách.',
          'Một phần mềm kế toán: chi phí làm sản phẩm gần như cố định, phục vụ thêm một khách chỉ tốn thêm máy chủ và hỗ trợ, có thể dưới 20.000 đồng mỗi tháng. Từ 300 lên 3.000 khách, [[bien-loi-nhuan-gop|biên lợi nhuận gộp]] tăng mạnh.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Nói "nền tảng công nghệ" mà không có số. Nhà đầu tư cần chi phí phục vụ một khách hôm nay và dự kiến khi quy mô tăng.',
          'Che phần làm thủ công phía sau. Làm tay lúc đầu là bình thường, miễn chỉ ra được phần nào sẽ tự động hoá và khi nào.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Kiểm tra chi phí trên mỗi khách bằng [[/cong-cu/kiem-tra-gia-dinh-kinh-te-don-vi|Kiểm tra kinh tế đơn vị]], rồi tự chấm mục này trong [[/cong-cu/diem-san-sang-goi-von|Điểm sẵn sàng gọi vốn]] và [[/cong-cu/pitch-deck-10-slide|Kiểm tra pitch deck]].',
        ],
      },
    ],
    faq: [
      { q: 'Đòn bẩy mở rộng là gì?', a: 'Là khả năng tăng khách và doanh thu nhanh hơn nhiều so với tăng chi phí, nhờ công nghệ hoặc tự động hoá.' },
      { q: 'Startup dịch vụ có đòn bẩy mở rộng không?', a: 'Có thể, nếu chuẩn hoá và tự động hoá được phần lớn việc phục vụ khách. Cần chỉ ra bằng số chi phí mỗi khách giảm khi quy mô tăng.' },
    ],
  },

  'hao-phong-thu': {
    capNhat: '2026-11-01',
    lienQuan: ['don-bay-mo-rong', 'product-market-fit', 'churn-rate', 'dang-ky-nhan-hieu'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Hào phòng thủ (moat) là lợi thế khiến đối thủ khó sao chép và khách khó rời đi. Đây là chữ Trenches trong 5T nhà đầu tư dùng để chấm startup. Câu hỏi luôn đến sau slide cạnh tranh: nếu bạn thành công, điều gì ngăn một công ty lớn làm y hệt.',
          'Đi trước vài tháng hay "chưa có đối thủ" không phải hào phòng thủ. Hào thật thường lớn dần cùng công ty, và cần bằng chứng bằng số.',
        ],
      },
      {
        h: 'Năm loại hào thường gặp',
        li: [
          'Dữ liệu riêng: càng nhiều khách dùng, dữ liệu càng tốt, sản phẩm càng chính xác hơn đối thủ mới.',
          'Hiệu ứng mạng: mỗi người dùng mới làm sản phẩm có giá trị hơn với người cũ, như sàn kết nối người mua và người bán.',
          'Chi phí chuyển đổi: khách đã đưa dữ liệu, quy trình và nhân viên vào sản phẩm, đổi sang chỗ khác tốn kém.',
          'Giấy phép hoặc tài sản trí tuệ: giấy phép khó xin, bằng sáng chế, [[dang-ky-nhan-hieu|nhãn hiệu]] đã đăng ký.',
          'Kênh phân phối độc quyền: hợp đồng hoặc quan hệ đối thủ không dễ có được.',
        ],
      },
      {
        h: 'Ví dụ',
        p: [
          'Một phần mềm quản lý tiệm tạp hoá có 5.000 tiệm dùng. Sau 12 tháng, mỗi tiệm đã lưu toàn bộ công nợ và lịch sử nhập hàng trong phần mềm, và tỷ lệ tiệm ngừng dùng chỉ khoảng 2% mỗi tháng. Đối thủ miễn phí vẫn khó kéo khách vì chuyển sang phải nhập lại từ đầu. Đó là chi phí chuyển đổi, có số [[churn-rate|tỷ lệ rời bỏ]] làm bằng chứng.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Viết "không có đối thủ". Nhà đầu tư hiểu là chưa tìm hiểu kỹ, vì khách luôn đang giải quyết vấn đề bằng cách nào đó.',
          'Liệt kê cả năm loại hào. Chọn một lợi thế đang thật sự xây, nói trong một câu, kèm một con số chứng minh.',
        ],
      },
      {
        h: 'Thử với số của bạn',
        p: [
          'Tự chấm mục hào phòng thủ trong [[/cong-cu/diem-san-sang-goi-von|Điểm sẵn sàng gọi vốn]] và [[/cong-cu/pitch-deck-10-slide|Kiểm tra pitch deck]]. Bài [[/playbook/toan-hoc-quy-vc-vi-sao-quy-tu-choi-startup-co-lai|Toán học của quỹ VC]] giải thích vì sao quỹ soi kỹ điểm này.',
        ],
      },
    ],
    faq: [
      { q: 'Hào phòng thủ của startup là gì?', a: 'Là lợi thế khiến đối thủ khó sao chép và khách khó rời đi, như dữ liệu riêng, hiệu ứng mạng, chi phí chuyển đổi, giấy phép hay kênh phân phối độc quyền.' },
      { q: 'Startup mới chưa có hào phòng thủ thì sao?', a: 'Ở vòng sớm, nhà đầu tư chấp nhận hào chưa sâu, nhưng muốn thấy bạn đang xây hào nào và dấu hiệu đầu tiên là gì, như tỷ lệ khách ở lại.' },
    ],
  },
};
